import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import {
  Box,
  Container,
  Paper,
  Chip,
  Grid2,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  IconButton,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Link,
  Tooltip,
} from "@mui/material";
import {
  Download as DownloadIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonOutline as PersonOutlineIcon,
  Update as UpdateIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  PurchaseRequestProduct,
  PurchaseRequestProductInsights,
} from "../../../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import {
  formatDate,
  parseUtcDate,
} from "../../../../../views/components/common/functions/formatDate";
import dayjs from "dayjs";
import SingleInput from "../../../../../views/components/common/pop-up/SingleInput";
import { GstPercentage } from "../../../../../models/Order/OrderStatus";
import pencil_uderline from "../../../../../assets/pencil_uderline.png";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import ShortClosePoDialog from "../../components/purchase-request/ShortClosePoDialog";
import { ShortClosePoLineInput } from "../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import {
  PurchaseOrderStatusOptions,
  PurchaseRequestRejectedBy,
} from "../../../../../models/PurchaseRequest/PurchaseRequestBody";
import PurchaseVouchersPopup from "../../components/purchase-request/PurchaseVouchersPopup";
import {
  PurchaseOrderInvoice,
  InvoiceUploadData,
} from "../../../../../models/Invoice/PurchaseOrderInvoice";
import { InvoicePaymentStatusFilters } from "../../../../../models/ProjectManagement/ProjectInventoryProduct";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import InvoiceUploadDialog from "../../components/purchase-request/InvoiceUploadDialog";
import { InvoiceItemsDetailsTable } from "../../components/purchase-request/InvoiceItemsDetailsTable";
import VendorPaymentPopup from "./VendorPaymentPopup";
import { VendorPaymentRow } from "../../../../../models/PurchaseRequest/VendorPayments";
import { VendorPaymentsStore } from "../../../stores/VendorPaymentsStore";
import {
  canEditPrProductFields,
  canGenerateOrRegeneratePurchaseOrder,
  canReactivatePurchaseOrder,
  hasActivePurchaseOrdersBlockingGenerate,
  isPoCancelledOrClosed,
  isPrPendingSourcingAndFinanceApproval,
} from "../../../../../utils/purchaseRequestPoHelpers";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { PurhcaseRequestDetails } from "../../../../../models/PurchaseRequest/PurchaseRequestDetails";
import {
  formatUrgentPaymentYesLabel,
  getUrgentPaymentWorkflowStatus,
} from "../../../../../utils/purchaseRequestUrgentPayment";
import {
  getPurchaseRequestLineItems,
  getPurchaseRequestProductIds,
  hasPurchaseRequestSkuVariants,
} from "../../../../../utils/purchaseRequestSkuVariants";
import { CatalogProductStore } from "../../../stores/CatalogProductStore";
import { getApiErrorMessage } from "../../../../../models/CatalogProducts/types";
import PrInlineEditableField from "../../components/purchase-request/PrInlineEditableField";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import PurchaseRequestActivityLogsDrawer from "./PurchaseRequestActivityLogsDrawer";

/** Catalog product fields (e.g. HSN via sourcing API). */
type PrInlineCatalogField = "hsn_code";

/** PR rate / GST fields editable at product scope. */
type PrInlineRateGstField = "rate" | "gst_percentage";

/** SKU-scoped editable fields. */
type PrInlineSkuField = PrInlineRateGstField | "quantity";

type SkuVariantGroup = {
  productId: number;
  productName: string;
  hsnCode: string | null;
  productRate: number | null;
  productGst: number | null;
  description: string | null;
  variants: NonNullable<PurhcaseRequestDetails["sku_variants"]>;
};

const prInlineCatalogFieldKey = (
  productId: number,
  field: PrInlineCatalogField,
): string => `catalog:${productId}:${field}`;

const prInlineProductFieldKey = (
  productId: number,
  field: PrInlineRateGstField | "remove",
): string => `product:${productId}:${field}`;

const prInlineSkuFieldKey = (
  sku: string,
  field: PrInlineSkuField | "remove",
): string => `sku:${sku.trim().toLowerCase()}:${field}`;

const parseNonNegativeNumber = (raw: string): number | null => {
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
};

const parsePositiveInteger = (raw: string): number | null => {
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) return null;
  return value;
};

const isLastProductOnPr = (groups: SkuVariantGroup[]): boolean =>
  groups.length <= 1;

const isLastSkuOfLastProduct = (
  groups: SkuVariantGroup[],
  productId: number,
  sku: string,
): boolean => {
  if (!isLastProductOnPr(groups)) return false;
  const group = groups.find((item) => item.productId === productId);
  if (!group) return false;
  if (group.variants.length !== 1) return false;
  return (
    (group.variants[0]?.sku || "").trim().toLowerCase() ===
    sku.trim().toLowerCase()
  );
};

const vendorPaymentsStore = new VendorPaymentsStore();
const EMPTY_IDS: number[] = [];

function vendorPaymentActorLabel(
  name: string | null | undefined,
  userId: number | null | undefined,
): string {
  const n = name?.trim();
  if (n) return n;
  if (userId != null) return `User #${userId}`;
  return "—";
}

/** True when the row was edited after creation (avoids duplicating create + update on first insert). */
function vendorPaymentWasEdited(payment: VendorPaymentRow): boolean {
  if (payment.updated_by === null) return false;
  if (payment.updated_by !== payment.created_by) return true;
  const created = payment.created_at
    ? new Date(payment.created_at).getTime()
    : 0;
  const updated = payment.updated_at
    ? new Date(payment.updated_at).getTime()
    : 0;
  return updated > created + 2000;
}

const PurchaseRequestDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const prId = searchParams.get("prId");
  const projectId = searchParams.get("projectId");
  const theme = useTheme();
  const {
    purchaseRequestStore,
    projectSourcingOpsStore,
    invoiceStore,
    userStore,
    warehouseStore,
  } = useStores();
  const { showSuccess, showError } = useNotification();
  const catalogProductStore = useRef(new CatalogProductStore()).current;
  const [isLoading, setIsLoading] = useState(false);
  /** Inline catalog field edits (keyed as `productId:field`). */
  const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingFieldKey, setSavingFieldKey] = useState<string | null>(null);
  const [purchaseVouchersPopupOpen, setPurchaseVouchersPopupOpen] =
    useState(false);
  const [selectedProductForVouchers, setSelectedProductForVouchers] =
    useState<PurchaseRequestProduct | null>(null);
  const [paymentStatusAnchor, setPaymentStatusAnchor] = useState<{
    element: HTMLElement;
    invoiceId: number;
  } | null>(null);
  const [invoiceDetailsPopup, setInvoiceDetailsPopup] = useState<{
    open: boolean;
    invoice: PurchaseOrderInvoice | null;
  }>({ open: false, invoice: null });

  // Sourcing approve/reject actions live on the details page (not in the listing)
  const [sourcingApprovePopupOpen, setSourcingApprovePopupOpen] =
    useState(false);
  const [sourcingRejectPopupOpen, setSourcingRejectPopupOpen] = useState(false);

  // Finance approve/reject actions live on the details page (not in the listing)
  const [financeApprovePopupOpen, setFinanceApprovePopupOpen] = useState(false);
  const [financeRejectPopupOpen, setFinanceRejectPopupOpen] = useState(false);

  // Swageazy approve/reject actions live on the details page (not in the listing)
  const [swageazyApprovePopupOpen, setSwageazyApprovePopupOpen] =
    useState(false);
  const [swageazyRejectPopupOpen, setSwageazyRejectPopupOpen] = useState(false);

  // Reset approval/rejection actions
  const [sourcingResetPopupOpen, setSourcingResetPopupOpen] = useState(false);
  const [financeResetPopupOpen, setFinanceResetPopupOpen] = useState(false);
  const [swageazyResetPopupOpen, setSwageazyResetPopupOpen] = useState(false);
  const [resetAllApprovalsPopupOpen, setResetAllApprovalsPopupOpen] =
    useState(false);

  // Cancel PR action live on the details page (not in the listing)
  const [cancelPRPopupOpen, setCancelPRPopupOpen] = useState(false);
  const [inwardCancelConfirm, setInwardCancelConfirm] = useState<{
    open: boolean;
    poId: number | null;
    reason: string;
  }>({ open: false, poId: null, reason: "" });
  const [reactivatePoPopupOpen, setReactivatePoPopupOpen] = useState(false);
  const [reactivatePoId, setReactivatePoId] = useState<number | null>(null);
  const [shortCloseTarget, setShortCloseTarget] = useState<{
    poId: number;
    preselectIds: number[];
  } | null>(null);
  const [markingUrgentPaymentDone, setMarkingUrgentPaymentDone] =
    useState(false);
  const [removeProductConfirm, setRemoveProductConfirm] = useState<{
    productId: number;
    productName: string;
  } | null>(null);
  const [removeSkuConfirm, setRemoveSkuConfirm] = useState<{
    sku: string;
    title: string;
    productId: number;
  } | null>(null);
  const purchaseOrdersSectionRef = useRef<HTMLDivElement | null>(null);
  const [activityLogsDrawerOpen, setActivityLogsDrawerOpen] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("");
  const [warehouseSaving, setWarehouseSaving] = useState(false);
  const [warehouseError, setWarehouseError] = useState("");

  useEffect(() => {
    if (!prId || projectId) return;
    const id = Number(prId);
    if (!Number.isInteger(id) || id < 1) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    purchaseRequestStore
      .getPurchaseRequestDetail(id)
      .then(() => {
        if (cancelled) return;
        const detail = purchaseRequestStore.purchaseRequestDetail;
        const orderId = detail?.order_id;
        if (
          !detail ||
          Number(detail.id) !== id ||
          orderId === undefined ||
          orderId === null
        ) {
          setIsLoading(false);
          return;
        }
        navigate(
          `/purchase/requests/details?prId=${encodeURIComponent(prId)}&projectId=${encodeURIComponent(String(orderId))}`,
          { replace: true },
        );
      })
      .catch((error) => {
        console.error("Error opening purchase request", error);
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, prId, projectId]);

  useEffect(() => {
    if (prId && projectId) {
      setIsLoading(true);
      purchaseRequestStore
        .getPurchaseRequestDetail(Number(prId))
        .then(() => {
          const detail = purchaseRequestStore.purchaseRequestDetail;
          const productIds = getPurchaseRequestProductIds(detail);
          purchaseRequestStore.clearSourcingInfoBySku();
          if (hasPurchaseRequestSkuVariants(detail)) {
            if (Number(projectId) > 0) {
              purchaseRequestStore
                .getSourcingInfoBySku(Number(projectId))
                .catch((error) => {
                  console.error("Error fetching sourcing info by SKU", error);
                });
            } else if (Number(projectId) === -1) {
              purchaseRequestStore
                .getSwageazyInventoryInwardBySku(Number(prId))
                .catch((error) => {
                  console.error(
                    "Error fetching Swageazy inventory inward by SKU",
                    error,
                  );
                });
            }
          }
          if (productIds.length > 0) {
            if (
              Number(projectId) > 0 &&
              userStore.hasPermission(
                [
                  EmployeeRoles.VIEW_COST_PRICE,
                  EmployeeRoles.CHANGE_COST_PRICE,
                ],
                true,
              )
            ) {
              projectSourcingOpsStore
                .getPurchaseRequestProductsInsight(
                  Number(projectId),
                  productIds,
                )
                .catch((error) => {
                  console.error(
                    "Error fetching purchase request product insights",
                    error,
                  );
                });
            }
            purchaseRequestStore
              .getPrProductAvailableQty(productIds, Number(projectId))
              .then(() => {
                setIsLoading(false);
              })
              .catch((error) => {
                console.error(
                  "Error fetching product available quantity",
                  error,
                );
              });
          }
        })
        .catch(() => {
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, projectId]);

  useEffect(() => {
    if (!prId || !userStore.hasPermission(EmployeeRoles.VIEW_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.getVendorPayments(Number(prId)).catch(() => {
      showError(
        "Error",
        "Failed to fetch vendor payments. Please try again later.",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, userStore]);

  useEffect(() => {
    warehouseStore.searchWarehouse().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shortCloseDetail = purchaseRequestStore.purchaseRequestDetail;
  const shortCloseInfoPrId = shortCloseDetail?.id ?? null;
  const shortCloseInfoPoId =
    shortCloseDetail?.purchase_orders?.find(
      (po) => !po.cancelled_by_admin_id && !isPoCancelledOrClosed(po.status),
    )?.id ??
    shortCloseDetail?.purchase_orders?.find(
      (po) => !po.cancelled_by_admin_id && po.status !== "CANCELLED",
    )?.id ??
    null;
  useEffect(() => {
    if (!shortCloseInfoPrId || !shortCloseInfoPoId) {
      return;
    }
    purchaseRequestStore
      .getShortClosePoLines(shortCloseInfoPrId, shortCloseInfoPoId)
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to fetch short close lines. Please try again later.",
        );
      });
  }, [purchaseRequestStore, shortCloseInfoPrId, shortCloseInfoPoId]);

  useEffect(() => {
    setSelectedWarehouseId(
      purchaseRequestStore.purchaseRequestDetail?.warehouse_id || "",
    );
  }, [purchaseRequestStore.purchaseRequestDetail?.warehouse_id]);

  const handleDownloadPO = (po_id: number) => {
    purchaseRequestStore
      .downloadPurchaseOrder(po_id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `PO_${po_id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download PO.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      });
  };

  const renderPurchaseOrderHistory = (productId: number) => {
    const productInsights =
      projectSourcingOpsStore.purchaseRequestProductInsights?.[productId] || [];

    return (
      <Box
        sx={{
          borderTop: `2px solid ${theme.palette.divider}`,
          pt: 4,
          mt: 4,
        }}
      >
        <Accordion
          sx={{
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: "8px 8px 0 0",
              "&.Mui-expanded": {
                minHeight: "auto",
                borderRadius: "8px 8px 0 0",
              },
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
                "&.Mui-expanded": {
                  margin: "12px 0",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.secondary.main + "15",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DownloadIcon sx={{ color: "secondary.main", fontSize: 20 }} />
              </Box>
              <CustomTypography variant="heading6" fontWeight={600}>
                Purchase Order History
              </CustomTypography>
              {productInsights.length > 0 && (
                <Chip
                  label={`${productInsights.length} items`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 3,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {productInsights.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 4,
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <ShoppingCartIcon
                  sx={{
                    fontSize: 48,
                    color: "text.secondary",
                    mb: 2,
                  }}
                />
                <CustomTypography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                >
                  No purchase order history available for this product
                </CustomTypography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {productInsights.map(
                  (insight: PurchaseRequestProductInsights, index: number) => (
                    <Box
                      key={`${insight.po_id}-${insight.pr_id}-${index}`}
                      sx={{
                        px: 2,
                        py: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                          transform: "translateY(-2px)",
                          borderColor: theme.palette.primary.main + "40",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: {
                            lg: "flex",
                            xl: "flex",
                            md: "flex",
                            sm: "block",
                            xs: "block",
                          },
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <CustomTypography variant="body1" fontWeight={600}>
                            {`${insight.title} - PR ${
                              insight.pr_id
                            } was raised for ${insight.customer_name} @ ${
                              insight.rate
                            } by ${
                              insight.created_by_admin_name
                            } on this ${formatDate(insight.created_date)}.`}
                          </CustomTypography>
                        </Box>
                        <Box
                          mt={{
                            xs: 2,
                            sm: 2,
                            md: 0,
                            lg: 0,
                            xl: 0,
                          }}
                        >
                          <AppButton
                            label={`Download PO-${insight.po_id}`}
                            variant="primary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadPO(insight.po_id)}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const handleSendEmail = (po_id: number) => {
    if (!prId) return;
    purchaseRequestStore
      .sendEmailToVendor(Number(prId), po_id)
      .then(() => {
        showSuccess(
          "Purchase order email has been sent to the vendor successfully.",
        );
      })
      .catch((error: any) => {
        showError(
          "Error Occurred",
          error?.response?.data?.message ||
            "Failed to send email, please try again later",
        );
      });
  };

  const getPoInwardedQty = (poId: number): number => {
    const inwardDetails =
      purchaseRequestStore.purchaseRequestDetail?.inward_details ?? [];
    return inwardDetails
      .filter((detail) => Number(detail.po_id) === Number(poId))
      .reduce(
        (sum, detail) => sum + (Number(detail.total_received_quantity) || 0),
        0,
      );
  };

  const clearPendingCancel = () => {
    setInwardCancelConfirm({ open: false, poId: null, reason: "" });
  };

  const handleStatusChange = (po_id: number, status: string) => {
    if (status === "CANCELLED") {
      purchaseRequestStore.openCancellationModal(po_id);
    } else {
      purchaseRequestStore.openStatusChangeModal(po_id, status);
    }
  };

  const handleInwardCancelConfirm = () => {
    if (inwardCancelConfirm.poId != null && inwardCancelConfirm.reason.trim()) {
      updatePOStatus(
        inwardCancelConfirm.poId,
        "CANCELLED",
        inwardCancelConfirm.reason,
      );
    }
    clearPendingCancel();
  };

  const handleInwardCancelClose = () => {
    clearPendingCancel();
  };

  const handleMarkUrgentPaymentDone = () => {
    const detail = purchaseRequestStore.purchaseRequestDetail;
    const prId = detail?.id;
    if (!prId || !detail) return;
    if (
      Boolean(detail.pr_cancellation_date) ||
      detail.active !== 1 ||
      detail.finance_approval !== 1
    ) {
      return;
    }
    setMarkingUrgentPaymentDone(true);
    purchaseRequestStore
      .markUrgentPaymentDone(prId)
      .then(() => {})
      .catch((error: any) => {
        showError(
          "Error Occurred",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to mark Urgent payment Paid.",
        );
      })
      .finally(() => setMarkingUrgentPaymentDone(false));
  };

  const updatePOStatus = (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    purchaseRequestStore
      .updatePOStatus(po_id, status, cancellationReason)
      .then((response) => {
        if (response.success) {
          showSuccess(`PO status updated to ${status} successfully.`);
          if (status === "CANCELLED") {
            const productIds = getPurchaseRequestProductIds(
              purchaseRequestStore.purchaseRequestDetail,
            );
            if (productIds?.length && projectId) {
              purchaseRequestStore.getPrProductAvailableQty(
                productIds,
                Number(projectId),
              );
            }
          }
        }
      })
      .catch((error: any) => {
        showError(
          "Error Occurred",
          error?.response?.data?.message ||
            "Failed to update PO status, please try again later",
        );
      });
  };

  const handleCancellationSubmit = (id: number, input?: string) => {
    if (!input || !input.trim()) {
      showError("Error", "Please enter a cancellation reason");
      return;
    }

    purchaseRequestStore.closeCancellationModal();

    if (getPoInwardedQty(id) > 0) {
      if (
        !userStore.hasPermission(
          EmployeeRoles.EDIT_PR_AND_PO_AFTER_PO_GENERATION,
        )
      ) {
        showError(
          "Error Occurred",
          "You do not have permission to cancel a PO after inward",
        );
        return;
      }
      setInwardCancelConfirm({
        open: true,
        poId: id,
        reason: input.trim(),
      });
      return;
    }

    updatePOStatus(id, "CANCELLED", input);
  };

  const handleCancellationClose = () => {
    purchaseRequestStore.closeCancellationModal();
  };

  const handleStatusChangeConfirm = (id: number) => {
    const status = purchaseRequestStore.statusChangeModal.status;
    if (status) {
      updatePOStatus(id, status);
      purchaseRequestStore.closeStatusChangeModal();
    }
  };

  const handleStatusChangeClose = () => {
    purchaseRequestStore.closeStatusChangeModal();
  };

  const handleApproveSourcingConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.SOURCING, userStore)
      .then(() => {
        setSourcingApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to approve sourcing. Please try again later.",
        );
      });
  };

  const handleApproveSourcingClose = () => {
    setSourcingApprovePopupOpen(false);
  };

  const handleRejectSourcingConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.SOURCING,
        userStore,
        rejectionReason,
      )
      .then(() => {
        setSourcingRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reject sourcing. Please try again later.",
        );
      });
  };

  const handleRejectSourcingClose = () => {
    setSourcingRejectPopupOpen(false);
  };

  const handleApproveFinanceConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.FINANCE, userStore)
      .then(() => {
        setFinanceApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to approve finance. Please try again later.",
        );
      });
  };

  const handleApproveFinanceClose = () => {
    setFinanceApprovePopupOpen(false);
  };

  const handleRejectFinanceConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.FINANCE,
        userStore,
        rejectionReason,
      )
      .then(() => {
        setFinanceRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reject finance. Please try again later.",
        );
      });
  };

  const handleRejectFinanceClose = () => {
    setFinanceRejectPopupOpen(false);
  };

  const handleApproveSwageazyConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.SWAGEAZY, userStore)
      .then(() => {
        setSwageazyApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to approve swageazy. Please try again later.",
        );
      });
  };

  const handleApproveSwageazyClose = () => {
    setSwageazyApprovePopupOpen(false);
  };

  const handleRejectSwageazyConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.SWAGEAZY,
        userStore,
        rejectionReason,
      )
      .then(() => {
        setSwageazyRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reject swageazy. Please try again later.",
        );
      });
  };

  const handleRejectSwageazyClose = () => {
    setSwageazyRejectPopupOpen(false);
  };

  const handleResetSourcingConfirm = (id: number) => {
    purchaseRequestStore
      .resetSourcingApproval(id)
      .then(() => {
        setSourcingResetPopupOpen(false);
        showSuccess("Sourcing approval/rejection reset successfully");
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reset sourcing approval. Please try again later.",
        );
      });
  };

  const handleResetFinanceConfirm = (id: number) => {
    purchaseRequestStore
      .resetFinanceApproval(id)
      .then(() => {
        setFinanceResetPopupOpen(false);
        showSuccess("Finance approval/rejection reset successfully");
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reset finance approval. Please try again later.",
        );
      });
  };

  const handleResetSwageazyConfirm = (id: number) => {
    purchaseRequestStore
      .resetSwageazyApproval(id)
      .then(() => {
        setSwageazyResetPopupOpen(false);
        showSuccess("Swageazy approval/rejection reset successfully");
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reset Swageazy approval. Please try again later.",
        );
      });
  };

  const handleResetAllApprovalsConfirm = (id: number) => {
    purchaseRequestStore
      .resetAllApprovals(id)
      .then(() => {
        setResetAllApprovalsPopupOpen(false);
        showSuccess(
          "All sourcing, finance, and Swageazy approvals/rejections reset successfully",
        );
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reset all approvals. Please try again later.",
        );
      });
  };

  const handleCancelPRClick = () => {
    if (!purchaseRequestStore.purchaseRequestDetail) return;

    // if (
    //   (purchaseRequestStore.purchaseRequestDetail.purchase_orders?.length || 0) >
    //   0
    // ) {
    //   showError(
    //     "Error",
    //     "Unable to cancel the PR because a PO has already been generated.",
    //   );
    //   return;
    // }

    if (purchaseRequestStore.purchaseRequestDetail.active_po === 1) {
      showError(
        "Error",
        "Unable to cancel the PR because it has an active PO assigned. Please close the PO before proceeding with the PR cancellation.",
      );
      return;
    }

    if (
      (purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
        ?.length ?? 0) > 0
    ) {
      showError(
        "Error",
        "Unable to cancel the PR because a vendor invoice has already been uploaded.",
      );
      return;
    }

    setCancelPRPopupOpen(true);
  };

  const handleCancelPRConfirm = (
    id: number,
    cancellationReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!cancellationReason || !cancellationReason.trim()) {
      showError("Error", "Please enter a cancellation reason");
      return;
    }

    purchaseRequestStore
      .cancelPurchaseRequest(id, userStore, cancellationReason)
      .then(() => {
        setCancelPRPopupOpen(false);
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to cancel the request. Please try again later.",
        );
      });
  };

  const handleCancelPRClose = () => {
    setCancelPRPopupOpen(false);
  };

  const handleOpenReactivatePo = (poId: number) => {
    setReactivatePoId(poId);
    setReactivatePoPopupOpen(true);
  };

  const handleReactivatePoClose = () => {
    setReactivatePoPopupOpen(false);
    setReactivatePoId(null);
  };

  const handleReactivatePOConfirm = (poId: number) => {
    const prIdNum = prId
      ? Number(prId)
      : purchaseRequestStore.purchaseRequestDetail?.id;
    if (!prIdNum || !poId) return;

    purchaseRequestStore
      .reactivatePO(prIdNum, poId)
      .then(() => {
        handleReactivatePoClose();
        const productIds = getPurchaseRequestProductIds(
          purchaseRequestStore.purchaseRequestDetail,
        );
        if (productIds?.length && projectId) {
          purchaseRequestStore.getPrProductAvailableQty(
            productIds,
            Number(projectId),
          );
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reactivate purchase order. Please try again later.",
        );
      });
  };

  const handleOpenShortClose = (poId: number, preselectIds: number[]) => {
    const prIdNum = prId
      ? Number(prId)
      : purchaseRequestStore.purchaseRequestDetail?.id;
    if (!prIdNum) return;
    setShortCloseTarget({ poId, preselectIds });
    purchaseRequestStore
      .getShortClosePoLines(prIdNum, poId)
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to load PO items for short close.",
        );
      });
  };

  const handleShortCloseSubmit = async (
    lines: ShortClosePoLineInput[],
    reason: string,
  ) => {
    const prIdNum = prId
      ? Number(prId)
      : purchaseRequestStore.purchaseRequestDetail?.id;
    if (!prIdNum || !shortCloseTarget) return;
    try {
      const response: any = await purchaseRequestStore.shortClosePO(
        prIdNum,
        shortCloseTarget.poId,
        lines,
        reason,
      );
      setShortCloseTarget(null);
      const closedCount = Array.isArray(response?.result?.lines)
        ? response.result.lines.length
        : lines.length;
      showSuccess(
        `Short closed ${closedCount} item${closedCount === 1 ? "" : "s"}`,
      );
      // Quantity changed: refresh balance qty and inwarded info like the initial load.
      const productIds = getPurchaseRequestProductIds(
        purchaseRequestStore.purchaseRequestDetail,
      );
      if (productIds?.length && projectId) {
        purchaseRequestStore
          .getPrProductAvailableQty(productIds, Number(projectId))
          .catch((error: any) => {
            showError(
              "Error",
              error?.response?.data?.message ||
                "Failed to refresh product available quantity. Please try again later.",
            );
          });
      }
      if (Number(projectId) > 0) {
        purchaseRequestStore
          .getSourcingInfoBySku(Number(projectId))
          .catch((error: any) => {
            showError(
              "Error",
              error?.response?.data?.message ||
                "Failed to refresh sourcing info. Please try again later.",
            );
          });
      } else if (Number(projectId) === -1) {
        purchaseRequestStore
          .getSwageazyInventoryInwardBySku(prIdNum)
          .catch((error: any) => {
            showError(
              "Error",
              error?.response?.data?.message ||
                "Failed to refresh inventory inward. Please try again later.",
            );
          });
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Failed to short close. Please try again later.",
      );
    }
  };

  const handleGeneratePO = () => {
    if (!purchaseRequestStore.purchaseRequestDetail) return;
    const detail = purchaseRequestStore.purchaseRequestDetail;

    if (!canGenerateOrRegeneratePurchaseOrder(detail)) {
      showError(
        "Error",
        "Generate PO is available only when there are no active purchase orders (Created, Open, or Closed).",
      );
      return;
    }

    purchaseRequestStore
      .generatePO(detail.id)
      .then(() => {
        const productIds = getPurchaseRequestProductIds(detail);
        if (productIds?.length && projectId) {
          purchaseRequestStore.getPrProductAvailableQty(
            productIds,
            Number(projectId),
          );
        }
        return purchaseRequestStore.getPurchaseRequestDetail(detail.id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to generate PO. Please try again later.",
        );
      });
  };

  const handleScrollToPurchaseOrders = () => {
    try {
      purchaseOrdersSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch (error) {
      showError("Error", "Could not scroll to Purchase Orders section.");
    }
  };

  const handleApproveInvoice = (invoiceId: number) => {
    invoiceStore.openApprovePurchaseOrderInvoicePopup(invoiceId);
  };

  const handleRejectInvoice = (invoiceId: number) => {
    invoiceStore.openRejectPurchaseOrderInvoicePopup(invoiceId);
  };

  const handleApproveInvoiceConfirm = (invoiceId: number) => {
    invoiceStore
      .approvePurchaseOrderInvoiceRequest(invoiceId)
      .then(() => {
        showSuccess("Invoice approved successfully");
        invoiceStore.closeApprovePurchaseOrderInvoicePopup();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to approve invoice. Please try again later.",
        );
      });
  };

  const handleRejectInvoiceConfirm = (
    invoiceId: number,
    rejectionReason?: string,
  ) => {
    invoiceStore
      .rejectPurchaseOrderInvoiceRequest(invoiceId, rejectionReason)
      .then(() => {
        showSuccess("Invoice rejected successfully");
        invoiceStore.closeRejectPurchaseOrderInvoicePopup();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to reject invoice. Please try again later.",
        );
      });
  };

  const handleRejectInvoiceClose = () => {
    invoiceStore.closeRejectPurchaseOrderInvoicePopup();
  };

  const handleApproveInvoiceClose = () => {
    invoiceStore.closeApprovePurchaseOrderInvoicePopup();
  };

  const handleMarkPaymentStatus = (
    invoiceId: number,
    paymentStatus: string,
  ) => {
    invoiceStore
      .markPurchaseOrderInvoicePaymentStatus(invoiceId, paymentStatus)
      .then(() => {
        showSuccess("Payment status updated successfully");
        setPaymentStatusAnchor(null);
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to update payment status. Please try again later.",
        );
      });
  };

  const handlePaymentStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    invoiceId: number,
  ) => {
    setPaymentStatusAnchor({ element: event.currentTarget, invoiceId });
  };

  const handlePaymentStatusClose = () => {
    setPaymentStatusAnchor(null);
  };

  const handlePaymentStatusSelect = (statusId: string) => {
    if (paymentStatusAnchor) {
      handleMarkPaymentStatus(paymentStatusAnchor.invoiceId, statusId);
    }
  };

  const handleOpenInvoiceUpload = (poId: number) => {
    if (!userStore.hasPermission(EmployeeRoles.UPLOAD_PURCHASE_ORDER_INVOICE)) {
      showError(
        "Access Denied",
        "You don't have permission to upload purchase order invoices.",
      );
      return;
    }
    const vendorId = purchaseRequestStore.purchaseRequestDetail?.vendor_id || 0;
    invoiceStore.openInvoiceUploadDialog(poId, vendorId);
  };

  const handleCloseInvoiceUpload = () => {
    invoiceStore.closeInvoiceUploadDialog();
  };

  const handleInvoiceUpload = async (uploadData: InvoiceUploadData) => {
    try {
      const response = await invoiceStore.invoiceAPI.uploadInvoice({
        po_id: uploadData.po_id,
        vendor_id: uploadData.vendor_id,
        invoice_url: uploadData.invoice_url,
        invoice_number: uploadData.invoice_number,
        invoice_amount: uploadData.invoice_amount,
        invoice_date: uploadData.invoice_date,
        invoice_items: uploadData.invoice_items || [],
        ...(uploadData.other_charges !== undefined && {
          other_charges: uploadData.other_charges,
        }),
      } as any);

      if (response.success) {
        showSuccess(response.message || "Invoice uploaded successfully");
        handleCloseInvoiceUpload();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      } else {
        showError("Error", response.message || "Failed to upload invoice");
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload invoice. Please try again later.",
      );
    }
  };

  const formatPaymentTimingLabel = (value: string) => {
    if (value === "post_invoice") return "Post Invoice";
    if (value === "advance") return "Advance";
    return value || "-";
  };

  const formatPaymentModeLabel = (mode: string | null | undefined) => {
    if (!mode?.trim()) return "Mode not provided";
    const labels: Record<string, string> = {
      check: "Cheque",
      razorpay: "Razorpay",
      stripe: "Stripe",
      creditcard: "Credit card",
      banktransfer: "Bank transfer",
      bankremittance: "Bank remittance",
      autotransaction: "Auto transaction",
      others: "Others",
    };
    const key = mode.trim();
    return labels[key] ?? key;
  };

  const totalPaidAmount = vendorPaymentsStore.payments.reduce(
    (sum, payment) => {
      const amount = Number(String(payment.amount ?? 0).replace(/,/g, ""));
      return Number.isNaN(amount) ? sum : sum + amount;
    },
    0,
  );
  const detailForTotals = purchaseRequestStore.purchaseRequestDetail;
  const hasSkuVariants = hasPurchaseRequestSkuVariants(detailForTotals);
  const purchaseRequestLineItems = getPurchaseRequestLineItems(detailForTotals);
  const currentGrandTotalAmount = (() => {
    const subtotal = purchaseRequestLineItems.reduce(
      (sum, item) =>
        sum + (item.rate || item.cost_price || 0) * (item.quantity || 0),
      0,
    );
    const totalGst = purchaseRequestLineItems.reduce((sum, item) => {
      const itemTotal =
        (item.rate || item.cost_price || 0) * (item.quantity || 0);
      return sum + (itemTotal * (item.gst_percentage || 0)) / 100;
    }, 0);
    const otherCharges =
      purchaseRequestStore.purchaseRequestDetail?.other_charges || 0;
    return Math.round((subtotal + totalGst + otherCharges) * 100) / 100;
  })();
  const editingPaymentAmountForPopup = Number(
    String(vendorPaymentsStore.popupState.payment?.amount ?? 0).replace(
      /,/g,
      "",
    ),
  );
  const adjustedEditingAmountForPopup = Number.isNaN(
    editingPaymentAmountForPopup,
  )
    ? 0
    : editingPaymentAmountForPopup;
  const maxAllowedPaymentAmount =
    Math.max(
      0,
      currentGrandTotalAmount -
        (totalPaidAmount - adjustedEditingAmountForPopup),
    ) + 1;

  const handleOpenCreateVendorPayment = () => {
    if (!userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.openCreatePopup();
  };

  const handleOpenEditVendorPayment = (payment: VendorPaymentRow) => {
    if (!userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.openEditPopup(payment);
  };

  const handleSaveVendorPayment = async (payload: {
    amount: number;
    payment_timing: "advance" | "post_invoice";
    payment_mode: string | null;
    remarks: string | null;
    payment_date: string | null;
    utr_no: string | null;
  }) => {
    if (
      !prId ||
      !userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS)
    )
      return;
    try {
      const existingPaidAmount = vendorPaymentsStore.payments.reduce(
        (sum, payment) => {
          const amount = Number(String(payment.amount ?? 0).replace(/,/g, ""));
          return Number.isNaN(amount) ? sum : sum + amount;
        },
        0,
      );
      const editingPaymentAmount = Number(
        String(vendorPaymentsStore.popupState.payment?.amount ?? 0).replace(
          /,/g,
          "",
        ),
      );
      const adjustedEditingAmount = Number.isNaN(editingPaymentAmount)
        ? 0
        : editingPaymentAmount;
      const nextTotalPaid =
        existingPaidAmount -
        adjustedEditingAmount +
        Number(payload.amount || 0);
      const grandTotalAmount = currentGrandTotalAmount;

      if (nextTotalPaid > grandTotalAmount + 1) {
        throw new Error(
          `Total paid amount cannot exceed grand total (₹${grandTotalAmount.toLocaleString("en-IN")}).`,
        );
      }

      await vendorPaymentsStore.saveVendorPayment(
        { ...payload, pr_id: Number(prId) },
        vendorPaymentsStore.popupState.payment?.id,
      );
      await vendorPaymentsStore.getVendorPayments(Number(prId));
      vendorPaymentsStore.closePopup();
      showSuccess(
        vendorPaymentsStore.popupState.payment
          ? "Vendor payment updated successfully."
          : "Vendor payment added successfully.",
      );
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          "Failed to save vendor payment. Please try again later.",
      );
    }
  };

  const handleDeleteVendorPayment = (paymentId: number) => {
    if (!userStore.hasPermission(EmployeeRoles.DELETE_VENDOR_PAYMENTS)) return;
    vendorPaymentsStore.openDeletePopup(paymentId);
  };

  const handleConfirmDeleteVendorPayment = async (id: number) => {
    if (!userStore.hasPermission(EmployeeRoles.DELETE_VENDOR_PAYMENTS)) return;
    try {
      await vendorPaymentsStore.deleteVendorPayment(id);
      showSuccess("Vendor payment deleted successfully.");
      vendorPaymentsStore.closeDeletePopup();
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Failed to delete vendor payment. Please try again later.",
      );
    }
  };

  const getStatusIcon = (approval: number, rejectionDate: string | null) => {
    if (rejectionDate) {
      return <CancelIcon sx={{ color: "error.main", fontSize: 24 }} />;
    }
    if (approval === 1) {
      return <CheckCircleIcon sx={{ color: "success.main", fontSize: 24 }} />;
    }
    return <PendingIcon sx={{ color: "warning.main", fontSize: 24 }} />;
  };

  const handleEditProduct = (
    product: PurchaseRequestProduct,
    field: string,
  ) => {
    const detail = purchaseRequestStore.purchaseRequestDetail;
    if (!detail || hasPurchaseRequestSkuVariants(detail)) return;

    const hasPostPoEditPermission = userStore.hasPermission(
      EmployeeRoles.EDIT_PR_AND_PO_AFTER_PO_GENERATION,
      true,
    );
    if (!canEditPrProductFields(detail, hasPostPoEditPermission)) {
      showError(
        "Access Denied",
        detail.po_id > 0
          ? "Cancel the purchase order first. Only authorized users can edit the PR after PO cancellation."
          : "This purchase request cannot be edited.",
      );
      return;
    }

    purchaseRequestStore.openEditDialog(product.product_id, field);
  };

  const handleModalSubmit = (
    productId: number,
    value: string | number,
    field: string,
  ) => {
    handleUpdateProductDetails(productId, value, field);
  };

  const handleUpdateProductDetails = (
    id: number,
    value: string | number,
    field: string,
  ) => {
    const detail = purchaseRequestStore.purchaseRequestDetail;
    if (!detail || hasPurchaseRequestSkuVariants(detail)) return;

    setIsLoading(true);
    const product = detail.products?.find((p) => p.product_id === id);

    if (product) {
      purchaseRequestStore
        .updateProductField(
          product,
          value,
          field,
          projectId ? Number(projectId) : undefined,
        )
        .then(() => {
          showSuccess("Product details updated successfully");
          if (prId) {
            return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
          }
        })
        .catch((err) => {
          const errorMessage =
            err?.response?.data?.message || err.response?.data?.message;
          showError("Error Occurred", errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const setInlineFieldError = (key: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [key]: message }));
  };

  const clearInlineFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const startInlineFieldEdit = (
    key: string,
    currentValue: string | number | null | undefined,
  ) => {
    setEditingFieldKey(key);
    setFieldDrafts((prev) => ({
      ...prev,
      [key]:
        currentValue === null || currentValue === undefined
          ? ""
          : String(currentValue),
    }));
    clearInlineFieldError(key);
  };

  const cancelInlineFieldEdit = (key: string) => {
    setEditingFieldKey(null);
    clearInlineFieldError(key);
  };

  const saveInlineHsnCode = async (productId: number) => {
    const key = prInlineCatalogFieldKey(productId, "hsn_code");
    const draft = (fieldDrafts[key] ?? "").trim();

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await catalogProductStore.updateSourcing(productId, {
        hsn_code: draft || null,
      });
      purchaseRequestStore.patchProductCatalogField(
        productId,
        "hsn_code",
        draft || null,
      );
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(
          error,
          "Failed to update HSN code. Please try again.",
        ),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const saveInlineProductRate = async (productId: number) => {
    const key = prInlineProductFieldKey(productId, "rate");
    const parsed = parseNonNegativeNumber(fieldDrafts[key] ?? "");
    if (parsed === null) {
      setInlineFieldError(key, "Enter a valid rate (0 or greater).");
      return;
    }
    if (!prId) {
      setInlineFieldError(key, "Purchase request id is missing.");
      return;
    }

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.updateProductRate(
        Number(prId),
        productId,
        parsed,
      );
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to update product rate."),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const saveInlineProductGst = async (productId: number) => {
    const key = prInlineProductFieldKey(productId, "gst_percentage");
    const parsed = parseNonNegativeNumber(fieldDrafts[key] ?? "");
    if (parsed === null) {
      setInlineFieldError(key, "Select a valid GST percentage.");
      return;
    }
    if (!prId) {
      setInlineFieldError(key, "Purchase request id is missing.");
      return;
    }

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.updateProductGst(
        Number(prId),
        productId,
        parsed,
      );
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to update product GST."),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const saveInlineSkuRate = async (sku: string) => {
    const key = prInlineSkuFieldKey(sku, "rate");
    const parsed = parseNonNegativeNumber(fieldDrafts[key] ?? "");
    if (parsed === null) {
      setInlineFieldError(key, "Enter a valid rate (0 or greater).");
      return;
    }
    if (!prId) {
      setInlineFieldError(key, "Purchase request id is missing.");
      return;
    }
    if (!sku.trim()) {
      setInlineFieldError(key, "SKU is missing.");
      return;
    }

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.updateSkuRate(Number(prId), sku, parsed);
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to update SKU rate."),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const saveInlineSkuGst = async (sku: string) => {
    const key = prInlineSkuFieldKey(sku, "gst_percentage");
    const parsed = parseNonNegativeNumber(fieldDrafts[key] ?? "");
    if (parsed === null) {
      setInlineFieldError(key, "Select a valid GST percentage.");
      return;
    }
    if (!prId) {
      setInlineFieldError(key, "Purchase request id is missing.");
      return;
    }
    if (!sku.trim()) {
      setInlineFieldError(key, "SKU is missing.");
      return;
    }

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.updateSkuGst(Number(prId), sku, parsed);
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to update SKU GST."),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const saveInlineSkuQuantity = async (sku: string) => {
    const key = prInlineSkuFieldKey(sku, "quantity");
    const parsed = parsePositiveInteger(fieldDrafts[key] ?? "");
    if (parsed === null) {
      setInlineFieldError(key, "Enter a whole-number quantity of at least 1.");
      return;
    }
    if (!prId) {
      setInlineFieldError(key, "Purchase request id is missing.");
      return;
    }
    if (!sku.trim()) {
      setInlineFieldError(key, "SKU is missing.");
      return;
    }

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.updateSkuQuantity(Number(prId), sku, parsed);
      setEditingFieldKey(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to update SKU quantity."),
      );
    } finally {
      setSavingFieldKey(null);
    }
  };

  const handleConfirmRemoveProduct = async () => {
    if (!removeProductConfirm || !prId) return;
    const { productId } = removeProductConfirm;
    const key = prInlineProductFieldKey(productId, "remove");

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.removeProduct(Number(prId), productId);
      setRemoveProductConfirm(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to remove product from PR."),
      );
      setRemoveProductConfirm(null);
    } finally {
      setSavingFieldKey(null);
    }
  };

  const handleConfirmRemoveSku = async () => {
    if (!removeSkuConfirm || !prId) return;
    const { sku } = removeSkuConfirm;
    const key = prInlineSkuFieldKey(sku, "remove");

    try {
      setSavingFieldKey(key);
      clearInlineFieldError(key);
      await purchaseRequestStore.removeSkuVariant(Number(prId), sku);
      setRemoveSkuConfirm(null);
    } catch (error) {
      setInlineFieldError(
        key,
        getApiErrorMessage(error, "Failed to remove SKU from PR."),
      );
      setRemoveSkuConfirm(null);
    } finally {
      setSavingFieldKey(null);
    }
  };

  if (isLoading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!purchaseRequestStore.purchaseRequestDetail) {
    return (
      <Container>
        <CustomTypography variant="heading6" color="error">
          Purchase Request not found
        </CustomTypography>
      </Container>
    );
  }

  const prDetail = purchaseRequestStore.purchaseRequestDetail;
  const isSwageazyInventoryPr =
    Number(projectId) === -1 || Number(prDetail?.order_id) === -1;
  const productsById = new Map(
    (prDetail.products || []).map((product) => [product.product_id, product]),
  );
  const skuVariantGroups = Array.from(
    (prDetail.sku_variants || [])
      .reduce((groups, variant) => {
        const existing = groups.get(variant.product_id);
        const product = productsById.get(variant.product_id);
        if (existing) {
          existing.variants.push(variant);
          if (!existing.description && variant.description?.trim()) {
            existing.description = variant.description.trim();
          }
        } else {
          groups.set(variant.product_id, {
            productId: variant.product_id,
            productName:
              variant.product_name?.trim() ||
              product?.product_name?.trim() ||
              `Product #${variant.product_id}`,
            hsnCode: product?.hsn_code ?? null,
            productRate: product?.rate ?? null,
            productGst: product?.gst_percentage ?? null,
            description:
              product?.description?.trim() ||
              variant.description?.trim() ||
              null,
            variants: [variant],
          });
        }
        return groups;
      }, new Map<number, SkuVariantGroup>())
      .values(),
  );
  const isLastProduct = isLastProductOnPr(skuVariantGroups);
  const isPRCancelled = Boolean(prDetail.pr_cancellation_date);
  const isAnyApprovalRejected =
    (Number(prDetail.rejected_by_finance_admin_id) || 0) > 0 ||
    (Number(prDetail.rejected_by_sourcing_admin_id) || 0) > 0 ||
    (Number(prDetail.rejected_by_swageazy_admin_id) || 0) > 0;
  const hasActivePoBlockingReset = (prDetail.purchase_orders ?? []).some(
    (po) => !isPoCancelledOrClosed(po.status),
  );
  const canShowResetActions = !isPRCancelled && !hasActivePoBlockingReset;
  const hasSourcingDecision =
    prDetail.sourcing_approval === 1 ||
    (Number(prDetail.rejected_by_sourcing_admin_id) || 0) > 0;
  const hasFinanceDecision =
    prDetail.finance_approval === 1 ||
    (Number(prDetail.rejected_by_finance_admin_id) || 0) > 0;
  const hasSwageazyDecision =
    prDetail.swageazy_approval === 1 ||
    (Number(prDetail.rejected_by_swageazy_admin_id) || 0) > 0;
  const hasAnyApprovalGranted =
    prDetail.finance_approval === 1 ||
    prDetail.sourcing_approval === 1 ||
    prDetail.swageazy_approval === 1;

  const urgentPaymentWorkflowStatus = getUrgentPaymentWorkflowStatus(prDetail);
  const hasMarkUrgentPaidPermission = userStore.hasPermission(
    EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
  );
  const canMarkUrgentPaymentPaid =
    hasMarkUrgentPaidPermission &&
    !isPRCancelled &&
    prDetail.active === 1 &&
    prDetail.finance_approval === 1;
  const urgentPaymentYesLabel = formatUrgentPaymentYesLabel(
    prDetail.urgent_payment_percentage,
  );
  const markUrgentPaymentDoneButtonSx = {
    minWidth: "auto",
    minHeight: 26,
    height: 26,
    px: 1.25,
    py: 0,
    fontSize: "0.72rem",
  };

  const hasPostPoEditPermission = userStore.hasPermission(
    EmployeeRoles.EDIT_PR_AND_PO_AFTER_PO_GENERATION,
    false,
  );
  const showProductEditIcon =
    !hasSkuVariants &&
    canEditPrProductFields(prDetail, hasPostPoEditPermission);
  const canEditCatalogHsn =
    userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS) &&
    isPrPendingSourcingAndFinanceApproval(prDetail);
  /** Rate/GST/qty/remove product|SKU — finance or sourcing PR action roles. */
  const canEditPrRateGst =
    userStore.hasPermission([
      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
      EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
    ]) && canEditPrProductFields(prDetail, hasPostPoEditPermission);
  const canViewActivityLogs = userStore.hasPermission([
    EmployeeRoles.PR_PO_VIEW,
    EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
    EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
    EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
  ]);
  const canEditWarehouseSelection =
    prDetail.active === 1 &&
    !isPRCancelled &&
    !hasAnyApprovalGranted &&
    userStore.hasPermission([
      EmployeeRoles.PR_PO_VIEW,
      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
      EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
      EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
    ]);
  const warehouseOptions = warehouseStore.warehouses.map((item) => ({
    id: String(item.id),
    value: item.identifier || String(item.id),
  }));
  const showReactivatePoAction = canReactivatePurchaseOrder(
    prDetail,
    hasPostPoEditPermission,
  );
  const canShortClosePo = userStore.hasPermission(EmployeeRoles.SHORT_CLOSE_PO);
  const shortClosablePoId =
    prDetail?.purchase_orders?.find(
      (po) => !po.cancelled_by_admin_id && !isPoCancelledOrClosed(po.status),
    )?.id ?? null;
  // Keyed by sku_variant_id (globally unique), so stale lines from another PR never match.
  const prSkuIds = new Set(
    (prDetail?.sku_variants ?? [])
      .map((sv) => sv.id)
      .filter((id): id is number => id != null),
  );
  const shortCloseInfoById = new Map(
    purchaseRequestStore.shortClosePoLines
      .filter((line) => prSkuIds.has(line.sku_variant_id))
      .map((line) => [line.sku_variant_id, line]),
  );
  const hasEligibleShortCloseLine = Array.from(
    shortCloseInfoById.values(),
  ).some((line) => line.eligible);
  const allPrLinesShortClosed =
    (prDetail?.sku_variants?.length ?? 0) > 0 &&
    Boolean(prDetail?.sku_variants?.every((sv) => sv.short_closed_qty != null));
  const formatShortClosedAt = (value?: string | null) => {
    const date = parseUtcDate(value);
    return date ? dayjs(date).format("DD MMM YYYY, hh:mm A") : null;
  };

  const handleUpdateWarehouse = async () => {
    if (!prId) return;
    if (!selectedWarehouseId) {
      setWarehouseError("Select a warehouse.");
      return;
    }
    const warehouse = warehouseStore.warehouses.find(
      (item) => String(item.id) === String(selectedWarehouseId),
    ) as
      | {
          address_line_1?: string | null;
          address_line_2?: string | null;
          landmark?: string | null;
          city?: string | null;
          state?: string | null;
          pincode?: string | null;
          country_code?: string | null;
          contact_name?: string | null;
          contact_phone?: string | null;
          gstin?: string | null;
        }
      | undefined;
    if (!warehouse) {
      setWarehouseError("Selected warehouse details were not found.");
      return;
    }
    setWarehouseError("");
    setWarehouseSaving(true);
    try {
      const res = await purchaseRequestStore.updateWarehouse(Number(prId), {
        warehouse_id: selectedWarehouseId,
        warehouse_address: {
          address_line_1: warehouse.address_line_1 ?? null,
          address_line_2: warehouse.address_line_2 ?? null,
          landmark: warehouse.landmark ?? null,
          city: warehouse.city ?? null,
          state: warehouse.state ?? null,
          pincode: warehouse.pincode ?? null,
          country_code: warehouse.country_code ?? null,
          contact_name: warehouse.contact_name ?? null,
          contact_phone: warehouse.contact_phone ?? null,
          gstin: warehouse.gstin ?? null,
        },
      });
      showSuccess(res?.message || "Warehouse updated successfully.");
      await purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to update warehouse."),
      );
    } finally {
      setWarehouseSaving(false);
    }
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str?.toUpperCase());
  };

  const calculateTotals = () => {
    if (purchaseRequestLineItems.length === 0) {
      return {
        subtotal: 0,
        totalGst: 0,
        otherCharges: 0,
        grandTotal: 0,
      };
    }

    let subtotal = 0;
    let totalGst = 0;

    purchaseRequestLineItems.forEach((item) => {
      const itemTotal =
        (item.rate || item.cost_price || 0) * (item.quantity || 0);
      subtotal += itemTotal;

      const gstAmount = (itemTotal * (item.gst_percentage || 0)) / 100;
      totalGst += gstAmount;
    });

    const otherCharges = detailForTotals?.other_charges || 0;
    const grandTotal = subtotal + totalGst + otherCharges;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalGst: Math.round(totalGst * 100) / 100,
      otherCharges,
      grandTotal: Math.round(grandTotal * 100) / 100,
    };
  };

  const totals = calculateTotals();
  const remainingGap = Number(totals?.grandTotal || 0) - totalPaidAmount;
  const remainingAmountToBePaid = remainingGap < 1 ? 0 : remainingGap;

  const getFilteredInvoices = () => {
    if (!purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices) {
      return [];
    }
    return purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices.filter(
      (invoice: PurchaseOrderInvoice) => {
        if (purchaseRequestStore.prFilter === "ALL") return true;
        if (purchaseRequestStore.prFilter === "PENDING") {
          return (
            !invoice.invoice_status ||
            invoice.invoice_status === null ||
            invoice.invoice_status === ""
          );
        }
        return invoice.invoice_status === purchaseRequestStore.prFilter;
      },
    );
  };

  const filteredInvoices = getFilteredInvoices();

  const calculateInvoiceStats = () => {
    const invoices =
      purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices || [];

    let totalUploaded = 0;
    let approved = 0;
    let pending = 0;
    let rejected = 0;
    let otherCharges = 0;

    invoices.forEach((invoice: PurchaseOrderInvoice) => {
      const invoiceAmount = Number(invoice.invoice_amount) || 0;
      const charges = Number(invoice.other_charges) || 0;

      totalUploaded += invoiceAmount;
      otherCharges += charges;

      if (
        invoice.approve_invoice !== null &&
        invoice.approve_invoice !== undefined
      ) {
        approved += invoiceAmount;
      } else if (
        invoice.reject_invoice !== null &&
        invoice.reject_invoice !== undefined
      ) {
        rejected += invoiceAmount;
      } else {
        pending += invoiceAmount;
      }
    });

    const round = (amount: number) => Math.round(amount * 100) / 100;

    return {
      totalUploaded: round(totalUploaded),
      approved: round(approved),
      pending: round(pending),
      rejected: round(rejected),
      otherCharges: round(otherCharges),
    };
  };

  const invoiceStats = calculateInvoiceStats();

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {/* Header with Go Back Button */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <AppButton
          label="Go back"
          variant="transparentPrimary"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.history.back()}
        />
      </Box>

      {/* Section 1: Purchase Request Details */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.primary.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.primary.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="primary.main"
            >
              Purchase Request Details
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              Basic information and approval status
            </CustomTypography>
          </Box>
        </Box>

        <Paper
          sx={{
            p: 4,
            background: theme.palette.customColors.white,
            borderRadius: 3,
            boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Main Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 4,
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box sx={{ flex: 1, minWidth: "300px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography
                  variant="heading3"
                  fontWeight={700}
                  color="primary.main"
                >
                  Purchase Request #
                  {purchaseRequestStore.purchaseRequestDetail.id}
                </CustomTypography>
                <Chip
                  label={`Order #${purchaseRequestStore.purchaseRequestDetail.order_id}`}
                  color="primary"
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    height: "32px",
                  }}
                />
                {canViewActivityLogs && (
                  <AppButton
                    label="Activity Logs"
                    variant="secondary"
                    startIcon={<HistoryIcon />}
                    onClick={() => setActivityLogsDrawerOpen(true)}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Created:</strong>{" "}
                  {formatDate(
                    purchaseRequestStore.purchaseRequestDetail.created_date,
                  )}
                </CustomTypography>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Created by:</strong>{" "}
                  {
                    purchaseRequestStore.purchaseRequestDetail
                      .created_by_adminname
                  }
                </CustomTypography>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Expected Delivery Date:</strong>{" "}
                  {formatDate(
                    purchaseRequestStore.purchaseRequestDetail
                      .expected_delivery_date,
                  )}
                </CustomTypography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CustomTypography variant="body1" color="text.secondary">
                    <strong>Urgent Payment:</strong>
                  </CustomTypography>
                  {urgentPaymentWorkflowStatus === "not_required" ? (
                    <CustomTypography variant="body1" color="text.secondary">
                      Not required
                    </CustomTypography>
                  ) : (
                    <>
                      <CustomTypography variant="body1" color="text.secondary">
                        {urgentPaymentYesLabel}
                      </CustomTypography>
                      {urgentPaymentWorkflowStatus === "done" ? (
                        <CheckCircleIcon
                          sx={{
                            fontSize: 20,
                            color: theme.palette.customColors.successGreen,
                          }}
                        />
                      ) : null}
                      {urgentPaymentWorkflowStatus === "done" &&
                      (prDetail.urgent_payment_marked_by_adminname ||
                        prDetail.urgent_payment_marked_at) ? (
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                        >
                          {prDetail.urgent_payment_marked_by_adminname
                            ? `Marked by ${prDetail.urgent_payment_marked_by_adminname}`
                            : "Marked"}
                          {prDetail.urgent_payment_marked_at
                            ? ` · ${formatDate(prDetail.urgent_payment_marked_at)}`
                            : ""}
                        </CustomTypography>
                      ) : null}
                      {urgentPaymentWorkflowStatus === "pending" &&
                      hasMarkUrgentPaidPermission ? (
                        <AppButton
                          label={
                            markingUrgentPaymentDone
                              ? "Saving..."
                              : "Mark payment Paid"
                          }
                          variant="primary"
                          disabled={
                            !canMarkUrgentPaymentPaid ||
                            markingUrgentPaymentDone
                          }
                          onClick={handleMarkUrgentPaymentDone}
                          customStyles={markUrgentPaymentDoneButtonSx}
                        />
                      ) : null}
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Approval Status Cards */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {[
                {
                  label: "Finance",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail.finance_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_finance,
                  color: theme.palette.info.main,
                },
                {
                  label: "Sourcing",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail
                      .sourcing_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_sourcing,
                  color: theme.palette.warning.main,
                },
                {
                  label: "Swageazy",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail
                      .swageazy_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_swageazy,
                  color: theme.palette.success.main,
                },
              ].map((status, index) => (
                <Box
                  key={status.label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    border: `2px solid ${status.color}20`,
                    borderRadius: 2,
                    bgcolor: `${status.color}08`,
                    minWidth: 120,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: `${status.color}15`,
                      transform: "translateY(-2px)",
                      boxShadow: `0px 4px 12px 0px ${status.color}30`,
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    {getStatusIcon(status.approval, status.rejectionDate)}
                  </Box>
                  <CustomTypography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    {status.label}
                  </CustomTypography>

                  {status.label === "Sourcing" &&
                    userStore.hasPermission(
                      EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.sourcing_approval) === false &&
                    (prDetail.rejected_by_sourcing_admin_id === null ||
                      prDetail.rejected_by_sourcing_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setSourcingApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setSourcingRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Sourcing" &&
                    userStore.hasPermission(
                      EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
                    ) &&
                    canShowResetActions &&
                    hasSourcingDecision && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Reset"
                          variant="secondary"
                          onClick={() => setSourcingResetPopupOpen(true)}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Finance" &&
                    userStore.hasPermission(
                      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.finance_approval) === false &&
                    (prDetail.rejected_by_finance_admin_id === null ||
                      prDetail.rejected_by_finance_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setFinanceApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setFinanceRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Finance" &&
                    userStore.hasPermission(
                      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                    ) &&
                    canShowResetActions &&
                    hasFinanceDecision && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Reset"
                          variant="secondary"
                          onClick={() => setFinanceResetPopupOpen(true)}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Swageazy" &&
                    userStore.hasPermission(
                      EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.third_approval_required) &&
                    !prDetail.swageazy_approval &&
                    (prDetail.rejected_by_swageazy_admin_id === null ||
                      prDetail.rejected_by_swageazy_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setSwageazyApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setSwageazyRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Swageazy" &&
                    userStore.hasPermission([
                      EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
                      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                    ]) &&
                    canShowResetActions &&
                    hasSwageazyDecision && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Reset"
                          variant="secondary"
                          onClick={() => setSwageazyResetPopupOpen(true)}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}
                </Box>
              ))}
            </Box>
          </Box>
          {purchaseRequestStore.purchaseRequestDetail
            .rejected_by_finance_admin_id ||
          purchaseRequestStore.purchaseRequestDetail
            .rejected_by_sourcing_admin_id ||
          purchaseRequestStore.purchaseRequestDetail
            .rejected_by_swageazy_admin_id ? (
            <Box
              sx={{
                backgroundColor: theme.palette.error.main + "15",
                border: `1px solid ${theme.palette.error.main}40`,
                borderRadius: 2,
                padding: 2,
                marginBottom: 2,
                borderLeft: `4px solid ${theme.palette.error.main}`,
              }}
            >
              <CustomTypography
                variant="body1"
                color="error.main"
                fontWeight={600}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Purchase Request Rejected
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color="error.main"
                sx={{ marginTop: 1 }}
              >
                <strong>Rejected by:</strong>{" "}
                {purchaseRequestStore.purchaseRequestDetail
                  .rejected_by_finance_adminname ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejected_by_sourcing_adminname ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejected_by_swageazy_adminname}{" "}
                <br />
                <strong>Rejected date:</strong>{" "}
                {formatDate(
                  purchaseRequestStore.purchaseRequestDetail
                    .rejection_date_by_finance ||
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_sourcing ||
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_swageazy,
                )}
              </CustomTypography>
            </Box>
          ) : null}

          {/* Details Grid */}
          <Grid2 container spacing={3}>
            {/* Vendor Information */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BusinessIcon
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Vendor Information
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Vendor Name
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {purchaseRequestStore.purchaseRequestDetail.vendor_name}
                      </CustomTypography>
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Email Address
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {
                          purchaseRequestStore.purchaseRequestDetail
                            .vendor_email
                        }
                      </CustomTypography>
                    </Box>
                    <Box>
                      {purchaseRequestStore.purchaseRequestDetail
                        .bill_from_gst && (
                        <>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Bill From
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .bill_from_gst
                            }{" "}
                            (
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .bill_from_state
                            }
                            )
                          </CustomTypography>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid2>

            {/* Payment Information */}
            {/* <Grid2 size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.success.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ color: "success.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Payment Details
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Expected Payment Date
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {purchaseRequestStore.purchaseRequestDetail
                          .expected_payment_date
                          ? formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .expected_payment_date
                            )
                          : "Not specified"}
                      </CustomTypography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid2> */}

            {/* Approval Information */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.warning.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AccountBalanceIcon
                        sx={{ color: "warning.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Approval Details
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {purchaseRequestStore.purchaseRequestDetail
                      .finance_approval === 1 &&
                      purchaseRequestStore.purchaseRequestDetail
                        .approved_by_finance_adminname && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Finance Approved By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="success.main"
                          >
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .approved_by_finance_adminname
                            }
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Approved Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .finance_approval_date,
                            )}
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_finance_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_finance_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Finance Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_finance_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_finance,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_finance && (
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                              fontWeight={500}
                              sx={{ mt: 0.5 }}
                            >
                              Rejection Reason:{" "}
                              {
                                purchaseRequestStore.purchaseRequestDetail
                                  .rejection_reason_by_finance
                              }
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                    {purchaseRequestStore.purchaseRequestDetail
                      .sourcing_approval === 1 &&
                      purchaseRequestStore.purchaseRequestDetail
                        .approved_by_sourcing_adminname && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Sourcing Approved By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="success.main"
                          >
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .approved_by_sourcing_adminname
                            }
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Approved Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .sourcing_approval_date,
                            )}
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_sourcing_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_sourcing_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Sourcing Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_sourcing_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_sourcing,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_sourcing && (
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                              fontWeight={500}
                              sx={{ mt: 0.5 }}
                            >
                              Rejection Reason:{" "}
                              {
                                purchaseRequestStore.purchaseRequestDetail
                                  .rejection_reason_by_sourcing
                              }
                            </CustomTypography>
                          )}
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .swageazy_approval === 1 && (
                      <Box>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mb: 0.5 }}
                        >
                          Swageazy Approved By
                        </CustomTypography>
                        <CustomTypography
                          variant="body1"
                          fontWeight={600}
                          color="success.main"
                        >
                          {purchaseRequestStore.purchaseRequestDetail
                            .approved_by_swageazy_adminname ?? "-"}
                        </CustomTypography>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Approved Date:{" "}
                          {formatDate(
                            purchaseRequestStore.purchaseRequestDetail
                              .swageazy_approval_date,
                          )}
                        </CustomTypography>
                      </Box>
                    )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_swageazy_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_swageazy_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Swageazy Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_swageazy_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_swageazy,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_swageazy && (
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                              fontWeight={500}
                              sx={{ mt: 0.5 }}
                            >
                              Rejection Reason:{" "}
                              {
                                purchaseRequestStore.purchaseRequestDetail
                                  .rejection_reason_by_swageazy
                              }
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                    {purchaseRequestStore.purchaseRequestDetail
                      .finance_approval !== 1 &&
                      (purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_finance_admin_id === null ||
                        purchaseRequestStore.purchaseRequestDetail
                          .rejected_by_finance_admin_id <= 0) &&
                      purchaseRequestStore.purchaseRequestDetail
                        .sourcing_approval !== 1 &&
                      (purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_sourcing_admin_id === null ||
                        purchaseRequestStore.purchaseRequestDetail
                          .rejected_by_sourcing_admin_id <= 0) && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Status
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="warning.main"
                          >
                            Pending Approvals
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .pr_cancellation_date ? (
                      <Box>
                        <CustomTypography
                          variant="body2"
                          color="error.main"
                          fontWeight={600}
                          sx={{ mt: 1 }}
                        >
                          Cancelled
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancellation Date:{" "}
                          {formatDate(
                            purchaseRequestStore.purchaseRequestDetail
                              .pr_cancellation_date,
                          )}
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancelled By:{" "}
                          {
                            purchaseRequestStore.purchaseRequestDetail
                              .pr_cancelled_by_admin_name
                          }
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="error.main"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancellation Reason:{" "}
                          {purchaseRequestStore.purchaseRequestDetail
                            .pr_cancellation_reason || "-"}
                        </CustomTypography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <AppButton
                              label="Cancel PR"
                              variant="secondary"
                              onClick={handleCancelPRClick}
                              disabled={
                                (purchaseRequestStore.purchaseRequestDetail
                                  .purchase_order_invoices?.length ?? 0) > 0 ||
                                purchaseRequestStore.purchaseRequestDetail
                                  .active_po === 1 ||
                                (
                                  purchaseRequestStore.purchaseRequestDetail
                                    .purchase_orders ?? []
                                ).some(
                                  (po) => !isPoCancelledOrClosed(po.status),
                                )
                              }
                              customStyles={{
                                width: "100%",
                                borderRadius: 2,
                              }}
                            />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            {hasActivePurchaseOrdersBlockingGenerate(
                              prDetail,
                            ) ? (
                              <AppButton
                                label="View Purchase Orders"
                                variant="secondary"
                                onClick={handleScrollToPurchaseOrders}
                                customStyles={{
                                  width: "100%",
                                  borderRadius: 2,
                                }}
                              />
                            ) : (
                              <AppButton
                                label={
                                  prDetail.po_id > 0
                                    ? "Regenerate"
                                    : "Generate PO"
                                }
                                variant="primary"
                                onClick={handleGeneratePO}
                                disabled={
                                  !prDetail.sourcing_approval ||
                                  !prDetail.finance_approval ||
                                  prDetail.active === 0 ||
                                  !canGenerateOrRegeneratePurchaseOrder(
                                    prDetail,
                                  )
                                }
                                customStyles={{
                                  width: "100%",
                                  borderRadius: 2,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {userStore.hasPermission(
                          EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                        ) &&
                          canShowResetActions &&
                          hasAnyApprovalGranted && (
                            <AppButton
                              label="Reset All Approvals"
                              variant="secondary"
                              onClick={() =>
                                setResetAllApprovalsPopupOpen(true)
                              }
                              customStyles={{
                                width: "100%",
                                borderRadius: 2,
                              }}
                            />
                          )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>
        </Paper>
      </Box>

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.warning.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      {/* Warehouse section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.warning.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.warning.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.warning.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="warning.main"
            >
              Warehouse
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              Delivery warehouse for this purchase request
            </CustomTypography>
          </Box>
        </Box>

        <Paper
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
          }}
        >
          <Grid2 container spacing={2} columns={12}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <CustomTypography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 0.5 }}
              >
                Warehouse
              </CustomTypography>
              <AppSearchDropdownMulti
                title="Select warehouse"
                isMultiSelect={false}
                local
                disabled={!canEditWarehouseSelection || warehouseSaving}
                list={warehouseOptions}
                value={
                  selectedWarehouseId
                    ? [
                        {
                          id: selectedWarehouseId,
                          value:
                            warehouseOptions.find(
                              (item) =>
                                String(item.id) === String(selectedWarehouseId),
                            )?.value || selectedWarehouseId,
                        },
                      ]
                    : []
                }
                handleSelect={(items) => {
                  setWarehouseError("");
                  setSelectedWarehouseId(String(items[0]?.id || ""));
                }}
              />
              {warehouseError ? (
                <InlineFormError message={warehouseError} />
              ) : null}
            </Grid2>
            <Grid2 size={{ xs: 12, md: 5 }}>
              <CustomTypography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 0.5 }}
              >
                Address
              </CustomTypography>
              <CustomTypography variant="body1" fontWeight={600}>
                {[
                  prDetail.warehouse_address?.address_line_1,
                  prDetail.warehouse_address?.address_line_2,
                  prDetail.warehouse_address?.landmark,
                  prDetail.warehouse_address?.city,
                  prDetail.warehouse_address?.state,
                  prDetail.warehouse_address?.pincode,
                  prDetail.warehouse_address?.country_code,
                ]
                  .filter(Boolean)
                  .join(", ") || "-"}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <CustomTypography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 0.5 }}
              >
                Contact / GST
              </CustomTypography>
              <CustomTypography variant="body1" fontWeight={600}>
                {[
                  prDetail.warehouse_address?.contact_name,
                  prDetail.warehouse_address?.contact_phone,
                  prDetail.warehouse_address?.gstin,
                ]
                  .filter(Boolean)
                  .join(" | ") || "-"}
              </CustomTypography>
            </Grid2>
            {canEditWarehouseSelection ? (
              <Grid2 size={{ xs: 12 }} display="flex" justifyContent="flex-end">
                <AppButton
                  label={warehouseSaving ? "Saving..." : "Save Warehouse"}
                  variant="primary"
                  onClick={() => {
                    void handleUpdateWarehouse();
                  }}
                  disabled={
                    warehouseSaving ||
                    !selectedWarehouseId ||
                    selectedWarehouseId === (prDetail.warehouse_id || "")
                  }
                />
              </Grid2>
            ) : null}
          </Grid2>
        </Paper>
      </Box>

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.warning.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      {/* Section 2: Purchase request line items */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.success.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.success.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.success.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShoppingCartIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="success.main"
            >
              {hasSkuVariants ? "SKU Variant Details" : "Product Details"}
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              {hasSkuVariants ? (
                <>
                  {skuVariantGroups.length}{" "}
                  {skuVariantGroups.length === 1 ? "product" : "products"} ·{" "}
                  {purchaseRequestLineItems.length}{" "}
                  {purchaseRequestLineItems.length === 1 ? "SKU" : "SKUs"}
                </>
              ) : (
                <>
                  {purchaseRequestLineItems.length}{" "}
                  {purchaseRequestLineItems.length === 1
                    ? "product"
                    : "products"}
                </>
              )}{" "}
              in this purchase request
            </CustomTypography>
          </Box>
        </Box>

        {!hasSkuVariants && showProductEditIcon && hasPostPoEditPermission && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Any changes made to the quantity or HSN code will not impact the
            associated uploaded vendor invoices.
          </Alert>
        )}
        {hasSkuVariants && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            This purchase request uses SKU-level line items. You can edit rate
            and GST at product level (all SKUs) or per SKU. Product-level
            updates can also recalculate linked vendor invoice amounts.
          </Alert>
        )}
        {hasSkuVariants && purchaseRequestStore.sourcingInfoBySkuError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {purchaseRequestStore.sourcingInfoBySkuError}
          </Alert>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        {hasSkuVariants &&
          skuVariantGroups.map((group, groupIndex) => {
            const groupQuantity = group.variants.reduce(
              (sum, variant) => sum + Number(variant.quantity || 0),
              0,
            );
            const productBalanceQty = purchaseRequestStore.getProductBalanceQty(
              group.productId,
            );
            const variantRates = group.variants.map((variant) =>
              Number(variant.rate ?? variant.cost_price ?? 0),
            );
            const variantGsts = group.variants.map((variant) =>
              Number(variant.gst_percentage ?? 0),
            );
            const sharedRate =
              group.productRate ??
              (variantRates.length > 0 &&
              variantRates.every((rate) => rate === variantRates[0])
                ? variantRates[0]
                : null);
            const sharedGst =
              group.productGst ??
              (variantGsts.length > 0 &&
              variantGsts.every((gst) => gst === variantGsts[0])
                ? variantGsts[0]
                : null);
            const productRateKey = prInlineProductFieldKey(
              group.productId,
              "rate",
            );
            const productGstKey = prInlineProductFieldKey(
              group.productId,
              "gst_percentage",
            );
            const hsnKey = prInlineCatalogFieldKey(group.productId, "hsn_code");
            const isEditingProductRate = editingFieldKey === productRateKey;
            const isEditingProductGst = editingFieldKey === productGstKey;
            const isEditingHsn = editingFieldKey === hsnKey;
            const hsnDisplay =
              group.hsnCode || group.variants[0]?.hsn_code || "";
            const voucherProduct = productsById.get(group.productId);
            const isVoucherActionEnabled =
              prDetail.finance_approval === 1 &&
              prDetail.sourcing_approval === 1 &&
              (prDetail.rejected_by_swageazy_admin_id === null ||
                prDetail.rejected_by_swageazy_admin_id === undefined);

            return (
              <Card
                key={group.productId}
                sx={{
                  mb: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                      pb: 2,
                      mb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <CustomTypography
                        variant="heading5"
                        color="primary.main"
                        fontWeight={700}
                      >
                        {groupIndex + 1}. {group.productName}
                      </CustomTypography>
                      <Link
                        href={`/products/${group.productId}`}
                        target="_blank"
                        underline="none"
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color="primary"
                          clickable
                          icon={
                            <OpenInNewIcon
                              sx={{ fontSize: "14px !important" }}
                            />
                          }
                          label={`Product ID: ${group.productId}`}
                          sx={{
                            cursor: "pointer",
                            px: 0.5,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.light + "50",
                              "& .MuiChip-icon": {
                                color: theme.palette.primary.light + "50",
                              },
                            },
                          }}
                        />
                      </Link>
                    </Box>
                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      <Chip
                        size="small"
                        label={`${group.variants.length} ${
                          group.variants.length === 1 ? "SKU" : "SKUs"
                        }`}
                      />
                      <Chip size="small" label={`Qty: ${groupQuantity}`} />
                      {!isSwageazyInventoryPr && productBalanceQty !== null && (
                        <Chip
                          size="small"
                          color="secondary"
                          variant="outlined"
                          label={`Balance Qty: ${productBalanceQty}`}
                        />
                      )}
                      {voucherProduct && Boolean(voucherProduct.is_voucher) && (
                        <>
                          {voucherProduct.voucher_count > 0 && (
                            <AppButton
                              variant="transparentPrimary"
                              label="View Vouchers"
                              onClick={() => {
                                window.open(
                                  `/vouchers?page_no=1&limit=30&sort_by=id&sort_type=DESC&order_id=${prDetail.order_id}`,
                                  "_blank",
                                );
                              }}
                              disabled={!isVoucherActionEnabled}
                              customStyles={{ minWidth: 140, height: 40 }}
                            />
                          )}
                          {voucherProduct.voucher_count <
                            voucherProduct.quantity && (
                            <AppButton
                              variant="primary"
                              label="Purchase"
                              onClick={() => {
                                setSelectedProductForVouchers(voucherProduct);
                                setPurchaseVouchersPopupOpen(true);
                              }}
                              disabled={!isVoucherActionEnabled}
                              customStyles={{ minWidth: 140, height: 40 }}
                            />
                          )}
                        </>
                      )}
                      {canEditPrRateGst && (
                        <IconButton
                          size="small"
                          disabled={
                            isLastProduct ||
                            Boolean(savingFieldKey) ||
                            isPRCancelled
                          }
                          onClick={() => {
                            const removeKey = prInlineProductFieldKey(
                              group.productId,
                              "remove",
                            );
                            if (isLastProduct) {
                              setInlineFieldError(
                                removeKey,
                                "Cannot remove the last product from this purchase request.",
                              );
                              return;
                            }
                            clearInlineFieldError(removeKey);
                            setRemoveProductConfirm({
                              productId: group.productId,
                              productName: group.productName,
                            });
                          }}
                          sx={{
                            color: theme.palette.customColors.greyTextColor,
                            "&:hover": {
                              backgroundColor: "#FEF2F2",
                              color: theme.palette.customColors.errorRed,
                            },
                          }}
                          title={
                            isLastProduct
                              ? "Cannot remove the last product"
                              : "Remove product"
                          }
                        >
                          {savingFieldKey ===
                          prInlineProductFieldKey(group.productId, "remove") ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <InlineFormError
                    message={
                      fieldErrors[
                        prInlineProductFieldKey(group.productId, "remove")
                      ]
                    }
                    sx={{ mt: 0, mb: 1.5 }}
                  />

                  <Grid2 container spacing={2} columns={12} sx={{ mb: 2 }}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <PrInlineEditableField
                        label="HSN Code"
                        displayValue={hsnDisplay || "-"}
                        canEdit={canEditCatalogHsn}
                        isEditing={isEditingHsn}
                        isSaving={savingFieldKey === hsnKey}
                        draft={fieldDrafts[hsnKey] ?? ""}
                        error={fieldErrors[hsnKey]}
                        inputKind="text"
                        disabledActions={Boolean(savingFieldKey)}
                        onStartEdit={() =>
                          startInlineFieldEdit(hsnKey, hsnDisplay)
                        }
                        onCancel={() => cancelInlineFieldEdit(hsnKey)}
                        onSave={() => void saveInlineHsnCode(group.productId)}
                        onDraftChange={(value) => {
                          clearInlineFieldError(hsnKey);
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [hsnKey]: value,
                          }));
                        }}
                      />
                      <CustomTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Product catalog HSN code
                      </CustomTypography>
                    </Grid2>
                  </Grid2>

                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {group.variants.map((variant, variantIndex) => {
                      const unitRate = variant.rate || variant.cost_price || 0;
                      const lineSubtotal = unitRate * (variant.quantity || 0);
                      const gstAmount =
                        (lineSubtotal * (variant.gst_percentage || 0)) / 100;
                      const lineTotal = lineSubtotal + gstAmount;
                      const skuBalanceQty =
                        purchaseRequestStore.getSkuBalanceQty(
                          group.productId,
                          variant,
                        );
                      const skuInwardedQty =
                        purchaseRequestStore.getSkuInwardedQty(
                          group.productId,
                          variant,
                        );
                      const poInwardedQty = isSwageazyInventoryPr
                        ? skuInwardedQty
                        : shortCloseInfoPoId != null && variant.id != null
                          ? shortCloseInfoById.get(variant.id)
                              ?.inwarded_quantity
                          : undefined;
                      const skuRateKey = prInlineSkuFieldKey(
                        variant.sku || "",
                        "rate",
                      );
                      const skuGstKey = prInlineSkuFieldKey(
                        variant.sku || "",
                        "gst_percentage",
                      );
                      const skuQtyKey = prInlineSkuFieldKey(
                        variant.sku || "",
                        "quantity",
                      );
                      const skuRemoveKey = prInlineSkuFieldKey(
                        variant.sku || "",
                        "remove",
                      );
                      const isLastSkuOnLastProduct = isLastSkuOfLastProduct(
                        skuVariantGroups,
                        group.productId,
                        variant.sku || "",
                      );

                      return (
                        <Paper
                          key={
                            variant.id ||
                            `${group.productId}-${variant.sku}-${variantIndex}`
                          }
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2 }}
                        >
                          <Grid2 container spacing={2} alignItems="flex-start">
                            <Grid2 size={{ xs: 12, md: 3 }}>
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                gap={1}
                              >
                                <Box minWidth={0}>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={700}
                                  >
                                    {variant.title || "Untitled SKU"}
                                  </CustomTypography>
                                  <SkuCopyRow sku={variant.sku} />
                                  {variant.short_closed_qty == null &&
                                    canShortClosePo &&
                                    shortClosablePoId &&
                                    variant.id != null &&
                                    shortCloseInfoById.get(variant.id)
                                      ?.eligible === true && (
                                      <Button
                                        size="small"
                                        sx={{ mt: 0.5, px: 0 }}
                                        onClick={() =>
                                          handleOpenShortClose(
                                            shortClosablePoId,
                                            [Number(variant.id)],
                                          )
                                        }
                                      >
                                        Short close
                                      </Button>
                                    )}
                                </Box>
                                {canEditPrRateGst &&
                                  Boolean(variant.sku?.trim()) && (
                                    <IconButton
                                      size="small"
                                      disabled={
                                        isLastSkuOnLastProduct ||
                                        Boolean(savingFieldKey) ||
                                        isPRCancelled
                                      }
                                      onClick={() => {
                                        if (isLastSkuOnLastProduct) {
                                          setInlineFieldError(
                                            skuRemoveKey,
                                            "Cannot remove the last SKU of the last product on this PR.",
                                          );
                                          return;
                                        }
                                        clearInlineFieldError(skuRemoveKey);
                                        setRemoveSkuConfirm({
                                          sku: variant.sku || "",
                                          title:
                                            variant.title ||
                                            variant.sku ||
                                            "SKU",
                                          productId: group.productId,
                                        });
                                      }}
                                      sx={{
                                        color:
                                          theme.palette.customColors
                                            .greyTextColor,
                                        flexShrink: 0,
                                        "&:hover": {
                                          backgroundColor: "#FEF2F2",
                                          color:
                                            theme.palette.customColors.errorRed,
                                        },
                                      }}
                                      title={
                                        isLastSkuOnLastProduct
                                          ? "Cannot remove the last SKU of the last product"
                                          : "Remove SKU"
                                      }
                                    >
                                      {savingFieldKey === skuRemoveKey ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  )}
                              </Box>
                              <InlineFormError
                                message={fieldErrors[skuRemoveKey]}
                                sx={{ mt: 0.5, mb: 0 }}
                              />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 9 }}>
                              <Grid2 container spacing={2} columns={12}>
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <PrInlineEditableField
                                    label="Quantity"
                                    displayValue={`${variant.quantity || 0} units`}
                                    canEdit={
                                      canEditPrRateGst &&
                                      Boolean(variant.sku?.trim())
                                    }
                                    isEditing={editingFieldKey === skuQtyKey}
                                    isSaving={savingFieldKey === skuQtyKey}
                                    draft={fieldDrafts[skuQtyKey] ?? ""}
                                    error={fieldErrors[skuQtyKey]}
                                    inputKind="number"
                                    disabledActions={Boolean(savingFieldKey)}
                                    onStartEdit={() =>
                                      startInlineFieldEdit(
                                        skuQtyKey,
                                        variant.quantity ?? 1,
                                      )
                                    }
                                    onCancel={() =>
                                      cancelInlineFieldEdit(skuQtyKey)
                                    }
                                    onSave={() =>
                                      void saveInlineSkuQuantity(
                                        variant.sku || "",
                                      )
                                    }
                                    onDraftChange={(value) => {
                                      clearInlineFieldError(skuQtyKey);
                                      setFieldDrafts((prev) => ({
                                        ...prev,
                                        [skuQtyKey]: value,
                                      }));
                                    }}
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <CustomTypography
                                    variant="body3"
                                    color="text.secondary"
                                  >
                                    Inwarded (PO)
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    {poInwardedQty ?? "-"}
                                  </CustomTypography>
                                </Grid2>
                                {skuInwardedQty !== null && (
                                  <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                    <CustomTypography
                                      variant="body3"
                                      color="text.secondary"
                                    >
                                      Inwarded (Order)
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body1"
                                      fontWeight={600}
                                      sx={{ display: "block", mt: 0.5 }}
                                    >
                                      {skuInwardedQty}
                                    </CustomTypography>
                                  </Grid2>
                                )}
                                {!isSwageazyInventoryPr &&
                                  skuBalanceQty !== null && (
                                    <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                      <CustomTypography
                                        variant="body3"
                                        color="text.secondary"
                                      >
                                        Balance Qty (Order)
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        {skuBalanceQty}
                                      </CustomTypography>
                                    </Grid2>
                                  )}
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <PrInlineEditableField
                                    label="SKU GST"
                                    displayValue={`${
                                      variant.gst_percentage || 0
                                    }%`}
                                    canEdit={
                                      canEditPrRateGst &&
                                      Boolean(variant.sku?.trim())
                                    }
                                    isEditing={editingFieldKey === skuGstKey}
                                    isSaving={savingFieldKey === skuGstKey}
                                    draft={fieldDrafts[skuGstKey] ?? ""}
                                    error={fieldErrors[skuGstKey]}
                                    inputKind="gst"
                                    disabledActions={Boolean(savingFieldKey)}
                                    onStartEdit={() =>
                                      startInlineFieldEdit(
                                        skuGstKey,
                                        variant.gst_percentage ?? 0,
                                      )
                                    }
                                    onCancel={() =>
                                      cancelInlineFieldEdit(skuGstKey)
                                    }
                                    onSave={() =>
                                      void saveInlineSkuGst(variant.sku || "")
                                    }
                                    onDraftChange={(value) => {
                                      clearInlineFieldError(skuGstKey);
                                      setFieldDrafts((prev) => ({
                                        ...prev,
                                        [skuGstKey]: value,
                                      }));
                                    }}
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <PrInlineEditableField
                                    label="SKU Rate"
                                    displayValue={`₹${Number(
                                      unitRate,
                                    ).toLocaleString("en-IN")}`}
                                    canEdit={
                                      canEditPrRateGst &&
                                      Boolean(variant.sku?.trim())
                                    }
                                    isEditing={editingFieldKey === skuRateKey}
                                    isSaving={savingFieldKey === skuRateKey}
                                    draft={fieldDrafts[skuRateKey] ?? ""}
                                    error={fieldErrors[skuRateKey]}
                                    inputKind="number"
                                    disabledActions={Boolean(savingFieldKey)}
                                    onStartEdit={() =>
                                      startInlineFieldEdit(skuRateKey, unitRate)
                                    }
                                    onCancel={() =>
                                      cancelInlineFieldEdit(skuRateKey)
                                    }
                                    onSave={() =>
                                      void saveInlineSkuRate(variant.sku || "")
                                    }
                                    onDraftChange={(value) => {
                                      clearInlineFieldError(skuRateKey);
                                      setFieldDrafts((prev) => ({
                                        ...prev,
                                        [skuRateKey]: value,
                                      }));
                                    }}
                                  />
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <CustomTypography
                                    variant="body3"
                                    color="text.secondary"
                                  >
                                    Selling Price
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    {`₹${Number(
                                      variant.selling_price || 0,
                                    ).toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}`}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                                  <CustomTypography
                                    variant="body3"
                                    color="text.secondary"
                                  >
                                    Line Total
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    {`₹${Number(lineTotal).toLocaleString(
                                      "en-IN",
                                      { maximumFractionDigits: 2 },
                                    )}`}
                                  </CustomTypography>
                                </Grid2>
                              </Grid2>
                            </Grid2>
                          </Grid2>
                          {variant.short_closed_qty != null &&
                            (() => {
                              const original =
                                variant.initial_qty ?? variant.quantity ?? 0;
                              const closedAt = variant.quantity || 0;
                              const closedOn = formatShortClosedAt(
                                variant.short_closed_at,
                              );
                              const byText = [
                                variant.short_closed_by_admin_name
                                  ? `By ${variant.short_closed_by_admin_name}`
                                  : null,
                                closedOn ? `on ${closedOn}` : null,
                              ]
                                .filter(Boolean)
                                .join(" ");
                              const noShrink = {
                                flexShrink: 0,
                                whiteSpace: "nowrap" as const,
                              };
                              return (
                                <Box
                                  sx={{
                                    width: "100%",
                                    mt: 1.5,
                                    pt: 1,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    display: "flex",
                                    flexWrap: { xs: "wrap", sm: "nowrap" },
                                    alignItems: "center",
                                    columnGap: 1,
                                    rowGap: 0.5,
                                    minWidth: 0,
                                    "& .MuiTypography-root": {
                                      fontSize: "0.85rem",
                                    },
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label="Short closed"
                                    sx={{
                                      height: 22,
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={noShrink}
                                  >
                                    {`Original ${original} · Closed at ${closedAt} · Short ${original - closedAt}`}
                                  </CustomTypography>
                                  {variant.short_close_reason && (
                                    <Tooltip title={variant.short_close_reason}>
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          minWidth: 0,
                                          flex: "0 1 auto",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {`· Reason: ${variant.short_close_reason}`}
                                      </CustomTypography>
                                    </Tooltip>
                                  )}
                                  {byText && (
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={noShrink}
                                    >
                                      {`· ${byText}`}
                                    </CustomTypography>
                                  )}
                                </Box>
                              );
                            })()}
                        </Paper>
                      );
                    })}
                  </Box>

                  {group.description && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                      >
                        Product Description
                      </CustomTypography>
                      <CustomTypography variant="body1" color="text.primary">
                        {group.description}
                      </CustomTypography>
                    </Box>
                  )}

                  {renderPurchaseOrderHistory(group.productId)}
                </CardContent>
              </Card>
            );
          })}

        {!hasSkuVariants &&
          purchaseRequestStore.purchaseRequestDetail.products?.map(
            (product, index) => {
              const productBalanceQty =
                purchaseRequestStore.getProductBalanceQty(product.product_id);
              return (
                <Card
                  key={product.product_id}
                  sx={{
                    mb: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.12)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Product Header */}
                    <Box sx={{ mb: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 3,
                          pb: 3,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CustomTypography
                              variant="heading5"
                              fontWeight={700}
                              color="primary.main"
                            >
                              {index + 1}.
                            </CustomTypography>
                            <CustomTypography
                              variant="heading5"
                              fontWeight={700}
                              color="primary.main"
                            >
                              {product.product_name}
                            </CustomTypography>
                            <Chip
                              label={`ID: ${product.product_id}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                            {/* <Chip
                            label={`SKU: ${product.sku || "N/A"}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          /> */}
                          </Box>

                          {Boolean(product.is_voucher) && (
                            <Box sx={{ display: "flex", gap: 2 }}>
                              {product.voucher_count > 0 && (
                                <AppButton
                                  variant="transparentPrimary"
                                  label="View Vouchers"
                                  onClick={() => {
                                    window.open(
                                      `/vouchers?page_no=1&limit=30&sort_by=id&sort_type=DESC&order_id=${
                                        purchaseRequestStore
                                          .purchaseRequestDetail!.order_id
                                      }`,
                                      "_blank",
                                    );
                                  }}
                                  disabled={
                                    !(
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.finance_approval === 1 &&
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.sourcing_approval === 1 &&
                                      (purchaseRequestStore
                                        .purchaseRequestDetail
                                        ?.rejected_by_swageazy_admin_id ===
                                        null ||
                                        purchaseRequestStore
                                          .purchaseRequestDetail
                                          ?.rejected_by_swageazy_admin_id ===
                                          undefined)
                                    )
                                  }
                                  customStyles={{ minWidth: 140, height: 40 }}
                                />
                              )}
                              {product.voucher_count < product.quantity && (
                                <AppButton
                                  variant="primary"
                                  label="Purchase"
                                  onClick={() => {
                                    setSelectedProductForVouchers(product);
                                    setPurchaseVouchersPopupOpen(true);
                                  }}
                                  disabled={
                                    !(
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.finance_approval === 1 &&
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.sourcing_approval === 1 &&
                                      (purchaseRequestStore
                                        .purchaseRequestDetail
                                        ?.rejected_by_swageazy_admin_id ===
                                        null ||
                                        purchaseRequestStore
                                          .purchaseRequestDetail
                                          ?.rejected_by_swageazy_admin_id ===
                                          undefined)
                                    )
                                  }
                                  customStyles={{ minWidth: 140, height: 40 }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Product Details Grid */}
                      <Grid2 container spacing={3}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              transition: "all 0.2s ease-in-out",
                              minHeight: "80px",
                              display: "flex",
                              flexDirection: "column",
                              "&:hover": {
                                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                borderColor: theme.palette.primary.main + "40",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                minHeight: "24px",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                HSN Code
                              </CustomTypography>
                              {showProductEditIcon && (
                                <IconButton
                                  sx={{
                                    width: "10px",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                    },
                                    "&:active": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                  onClick={() =>
                                    handleEditProduct(product, "hsn_code")
                                  }
                                >
                                  <Icon sx={{ fontSize: "12px" }}>
                                    <img
                                      src={pencil_uderline}
                                      alt="edit-icon"
                                    />
                                  </Icon>
                                </IconButton>
                              )}
                            </Box>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {product.hsn_code || "N/A"}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              transition: "all 0.2s ease-in-out",
                              minHeight: "80px",
                              display: "flex",
                              flexDirection: "column",
                              "&:hover": {
                                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                borderColor: theme.palette.info.main + "40",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                minHeight: "24px",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                Quantity
                              </CustomTypography>
                              {showProductEditIcon && (
                                <IconButton
                                  sx={{
                                    width: "10px",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                    },
                                    "&:active": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                  onClick={() =>
                                    handleEditProduct(product, "quantity")
                                  }
                                >
                                  <Icon sx={{ fontSize: "12px" }}>
                                    <img
                                      src={pencil_uderline}
                                      alt="edit-icon"
                                    />
                                  </Icon>
                                </IconButton>
                              )}
                            </Box>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {product.quantity} units
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              transition: "all 0.2s ease-in-out",
                              minHeight: "80px",
                              display: "flex",
                              flexDirection: "column",
                              "&:hover": {
                                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                borderColor: theme.palette.success.main + "40",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                minHeight: "24px",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                Rate
                              </CustomTypography>
                              {showProductEditIcon && (
                                <IconButton
                                  sx={{
                                    width: "10px",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                    },
                                    "&:active": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                  onClick={() =>
                                    handleEditProduct(product, "rate")
                                  }
                                >
                                  <Icon sx={{ fontSize: "12px" }}>
                                    <img
                                      src={pencil_uderline}
                                      alt="edit-icon"
                                    />
                                  </Icon>
                                </IconButton>
                              )}
                            </Box>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              ₹{product.rate}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              transition: "all 0.2s ease-in-out",
                              minHeight: "80px",
                              display: "flex",
                              flexDirection: "column",
                              "&:hover": {
                                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                borderColor: theme.palette.warning.main + "40",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                minHeight: "24px",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                Selling Price
                              </CustomTypography>
                              {/* <IconButton
                              sx={{
                                width: "10px",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                                "&:active": {
                                  backgroundColor: "transparent",
                                },
                              }}
                              onClick={() =>
                                handleEditProduct(product, "selling_price")
                              }
                            >
                              <Icon sx={{ fontSize: "12px" }}>
                                <img src={pencil_uderline} alt="edit-icon" />
                              </Icon>
                            </IconButton> */}
                            </Box>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {product.selling_price != null
                                ? `₹${Number(
                                    product.selling_price,
                                  ).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}`
                                : "N/A"}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                          <Box
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              transition: "all 0.2s ease-in-out",
                              minHeight: "80px",
                              display: "flex",
                              flexDirection: "column",
                              "&:hover": {
                                boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                borderColor: theme.palette.error.main + "40",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                minHeight: "24px",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                GST %
                              </CustomTypography>
                              {showProductEditIcon &&
                                (showReactivatePoAction ||
                                  !(
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.swageazy_approval === 1 ||
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.sourcing_approval === 1 ||
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.finance_approval === 1
                                  )) && (
                                  <IconButton
                                    sx={{
                                      width: "10px",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                      },
                                      "&:active": {
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                    onClick={() =>
                                      handleEditProduct(
                                        product,
                                        "gst_percentage",
                                      )
                                    }
                                  >
                                    <Icon sx={{ fontSize: "12px" }}>
                                      <img
                                        src={pencil_uderline}
                                        alt="edit-icon"
                                      />
                                    </Icon>
                                  </IconButton>
                                )}
                            </Box>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {product.gst_percentage || "N/A"}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        {!isSwageazyInventoryPr &&
                          productBalanceQty !== null && (
                            <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                              <Box
                                sx={{
                                  p: 2.5,
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 2,
                                  backgroundColor:
                                    theme.palette.background.paper,
                                  transition: "all 0.2s ease-in-out",
                                  minHeight: "80px",
                                  display: "flex",
                                  flexDirection: "column",
                                  "&:hover": {
                                    boxShadow:
                                      "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                    borderColor:
                                      theme.palette.secondary.main + "40",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    minHeight: "24px",
                                    mb: 1,
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Balance Qty
                                  </CustomTypography>
                                </Box>
                                <CustomTypography
                                  variant="body1"
                                  fontWeight={600}
                                  color="text.primary"
                                >
                                  {productBalanceQty}
                                </CustomTypography>
                              </Box>
                            </Grid2>
                          )}
                      </Grid2>

                      {/* Product Description */}
                      {product.description && (
                        <Box
                          sx={{
                            mt: 4,
                            p: 3,
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ mb: 1.5 }}
                          >
                            Product Description
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            color="text.primary"
                          >
                            {product.description}
                          </CustomTypography>
                        </Box>
                      )}
                    </Box>

                    {/* PO History Section - Accordion */}
                    {renderPurchaseOrderHistory(product.product_id)}
                  </CardContent>
                </Card>
              );
            },
          )}
      </Box>

      {/* Section 3: Purchase Orders */}
      {purchaseRequestStore.purchaseRequestDetail.purchase_orders &&
        purchaseRequestStore.purchaseRequestDetail.purchase_orders.length >
          0 && (
          <>
            <Box
              sx={{
                height: 2,
                background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.warning.main} 50%, ${theme.palette.info.main} 100%)`,
                borderRadius: 1,
                mb: 4,
              }}
            />
            <Box
              ref={purchaseOrdersSectionRef}
              id="purchase-orders-section"
              sx={{ mb: 4 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  backgroundColor: theme.palette.warning.main + "08",
                  borderRadius: 2,
                  border: `2px solid ${theme.palette.warning.main}20`,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.warning.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DownloadIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Box>
                  <CustomTypography
                    variant="heading5"
                    fontWeight={700}
                    color="warning.main"
                  >
                    Purchase Orders
                  </CustomTypography>
                  <CustomTypography variant="body2" color="text.secondary">
                    {
                      purchaseRequestStore.purchaseRequestDetail.purchase_orders
                        .length
                    }{" "}
                    PO created
                  </CustomTypography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {purchaseRequestStore.purchaseRequestDetail.purchase_orders.map(
                  (order, index) => (
                    <Card
                      key={order.id}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: { xs: 3, sm: 2 },
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.secondary.main,
                              }}
                            />
                            <CustomTypography
                              variant="heading6"
                              fontWeight={600}
                              color="text.primary"
                            >
                              PO #{order.id}
                            </CustomTypography>
                            <Chip
                              label={order.status}
                              size="small"
                              color={
                                order.status === "CREATED"
                                  ? "info"
                                  : order.status === "OPEN"
                                    ? "info"
                                    : order.status === "SENT"
                                      ? "success"
                                      : order.status === "CANCELLED"
                                        ? "error"
                                        : "default"
                              }
                              variant="outlined"
                            />
                            {order.status !== "CANCELLED" &&
                              order.status !== "CLOSED" && (
                                <AppDropDown
                                  title="Update Status"
                                  isMultiSelect={false}
                                  list={PurchaseOrderStatusOptions}
                                  value={order.status}
                                  onSingleSelectionChange={(status) =>
                                    handleStatusChange(order.id, status)
                                  }
                                  disabled={purchaseRequestStore.updatingStatus.has(
                                    order.id,
                                  )}
                                  width="120px"
                                  height="32px"
                                  fontSize="0.8rem"
                                />
                              )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",

                              gap: { xs: 1, sm: 2 },
                              flexWrap: "wrap",
                            }}
                          >
                            {showReactivatePoAction &&
                              isPoCancelledOrClosed(order.status) && (
                                <AppButton
                                  label="Reactivate PO"
                                  variant="primary"
                                  onClick={() =>
                                    handleOpenReactivatePo(order.id)
                                  }
                                  customStyles={{
                                    minWidth: "auto",
                                    px: 2,
                                    py: 0.5,
                                    height: "32px",
                                  }}
                                />
                              )}
                            {allPrLinesShortClosed &&
                              !order.cancelled_by_admin_id &&
                              order.status !== "CANCELLED" && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label="Short closed"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            {canShortClosePo &&
                              !order.cancelled_by_admin_id &&
                              !isPoCancelledOrClosed(order.status) &&
                              order.id === shortClosablePoId &&
                              hasEligibleShortCloseLine && (
                                <AppButton
                                  label="Short close PO"
                                  variant="secondary"
                                  onClick={() =>
                                    handleOpenShortClose(order.id, [])
                                  }
                                  customStyles={{
                                    minWidth: "auto",
                                    px: 2,
                                    py: 0.5,
                                    height: "32px",
                                  }}
                                />
                              )}
                            {!order.cancelled_by_admin_id &&
                              order.status !== "CANCELLED" &&
                              order.status !== "CLOSED" && (
                                <>
                                  <IconButton
                                    onClick={() => handleDownloadPO(order.id)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      backgroundColor:
                                        theme.palette.primary.main + "15",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.main + "25",
                                        transform: "scale(1.05)",
                                      },
                                      flexShrink: 0,
                                    }}
                                  >
                                    <DownloadIcon
                                      sx={{
                                        color: "primary.main",
                                        fontSize: 16,
                                      }}
                                    />
                                  </IconButton>
                                  {userStore.hasPermission(
                                    EmployeeRoles.UPLOAD_PURCHASE_ORDER_INVOICE,
                                  ) && (
                                    <AppButton
                                      label="Upload Invoice"
                                      onClick={() =>
                                        handleOpenInvoiceUpload(order.id)
                                      }
                                      variant="primary"
                                      customStyles={{
                                        minWidth: "auto",
                                        px: 2,
                                        py: 0.5,
                                      }}
                                    />
                                  )}
                                  <AppButton
                                    label={
                                      purchaseRequestStore.sendingEmails.has(
                                        order.id,
                                      ) ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress
                                            size={14}
                                            sx={{ color: "success.main" }}
                                          />
                                          {order.email_sent
                                            ? "Resending..."
                                            : "Sending..."}
                                        </Box>
                                      ) : order.email_sent ? (
                                        "Resend"
                                      ) : (
                                        "Send Email"
                                      )
                                    }
                                    onClick={() => handleSendEmail(order.id)}
                                    disabled={purchaseRequestStore.sendingEmails.has(
                                      order.id,
                                    )}
                                    variant="secondary"
                                    customStyles={{
                                      minWidth: "auto",
                                      px: 2,
                                      py: 0.5,
                                      height: "32px",
                                      fontSize: "0.75rem",
                                      borderColor: theme.palette.success.main,
                                      whiteSpace: "nowrap",
                                      "@media (max-width: 600px)": {
                                        fontSize: "0.7rem",
                                        px: 1.5,
                                        minWidth: "100px",
                                      },
                                      "&:disabled": {
                                        borderColor: theme.palette.grey[400],
                                        color: theme.palette.grey[500],
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  />
                                </>
                              )}
                          </Box>
                        </Box>

                        <Grid2 container spacing={2}>
                          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                sx={{ mb: 0.5 }}
                              >
                                Created By - {order.created_by_adminname}
                              </CustomTypography>
                            </Box>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 3.5 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                style={{ marginBottom: 0.5 }}
                              >
                                Created Date -{" "}
                                {order.created_date
                                  ? formatDate(order.created_date)
                                  : "-"}
                              </CustomTypography>
                            </Box>
                          </Grid2>

                          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                style={{ marginBottom: 0.5 }}
                              >
                                Email Status -{" "}
                                {order.email_sent
                                  ? `Sent on ${formatDate(
                                      order.email_sent_date,
                                    )}`
                                  : "Pending"}
                              </CustomTypography>
                            </Box>
                          </Grid2>
                        </Grid2>

                        {order.cancellation_reason && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: theme.palette.error.main + "10",
                              borderRadius: 1,
                              border: `1px solid ${
                                theme.palette.error.main + "30"
                              }`,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                              fontWeight={500}
                              sx={{ mb: 0.5 }}
                            >
                              Cancellation Reason
                            </CustomTypography>
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                            >
                              {order.cancellation_reason}
                            </CustomTypography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ),
                )}
              </Box>
            </Box>
          </>
        )}

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.info.main} 50%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      <>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.purple.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.purple.main}20`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.info.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading5"
                  fontWeight={700}
                  color="info.main"
                >
                  Payments
                </CustomTypography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Chip
                label={`Total Paid Amount: INR ${totalPaidAmount.toLocaleString("en-IN")}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontWeight: 600, backgroundColor: "background.paper" }}
              />
              <AppButton
                label="Add Payment"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateVendorPayment}
                disabled={
                  !userStore.hasPermission(
                    EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS,
                  ) ||
                  isPRCancelled ||
                  isAnyApprovalRejected
                }
                customStyles={{
                  borderRadius: "10px",
                  px: 2,
                  boxShadow: "0 8px 16px rgba(99, 102, 241, 0.20)",
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gap: 2 }}>
            {!userStore.hasPermission(EmployeeRoles.VIEW_VENDOR_PAYMENTS) ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                  backgroundColor:
                    theme.palette.customColors.greyBodyBackground,
                }}
              >
                <CustomTypography variant="body1" fontWeight={600}>
                  You do not have access to view vendor payments.
                </CustomTypography>
              </Box>
            ) : vendorPaymentsStore.loading ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                }}
              >
                <CustomTypography variant="body2" color="text.secondary">
                  Loading vendor payments...
                </CustomTypography>
              </Box>
            ) : vendorPaymentsStore.payments.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                  backgroundColor:
                    theme.palette.customColors.greyBodyBackground,
                }}
              >
                <CustomTypography variant="body1" fontWeight={600}>
                  No vendor payments added yet
                </CustomTypography>
                <CustomTypography variant="body2" color="text.secondary">
                  Add advance or post-invoice payment entries for this purchase
                  request.
                </CustomTypography>
              </Box>
            ) : (
              vendorPaymentsStore.payments.map((payment) => (
                <Card
                  key={payment.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            rowGap: 1,
                          }}
                        >
                          <CustomTypography
                            variant="heading6"
                            fontWeight={700}
                            color="primary.main"
                            sx={{ lineHeight: 1.2 }}
                          >
                            INR{" "}
                            {Number(payment.amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </CustomTypography>
                          <Chip
                            label={formatPaymentTimingLabel(
                              payment.payment_timing,
                            )}
                            color={
                              payment.payment_timing === "advance"
                                ? "warning"
                                : "info"
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            label={formatPaymentModeLabel(payment.payment_mode)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                        >
                          Payment date:{" "}
                          {payment.payment_date
                            ? formatDate(payment.payment_date)
                            : "—"}
                          {" · "}
                          UTR: {payment.utr_no?.trim() || "—"}
                        </CustomTypography>
                        {payment.remarks?.trim() ? (
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                          >
                            Remarks: {payment.remarks.trim()}
                          </CustomTypography>
                        ) : null}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {userStore.hasPermission(
                          EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS,
                        ) ? (
                          <IconButton
                            onClick={() => handleOpenEditVendorPayment(payment)}
                            sx={{
                              borderRadius: 1.5,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        {userStore.hasPermission(
                          EmployeeRoles.DELETE_VENDOR_PAYMENTS,
                        ) ? (
                          <IconButton
                            onClick={() =>
                              handleDeleteVendorPayment(payment.id)
                            }
                            sx={{
                              borderRadius: 1.5,
                              border: `1px solid ${theme.palette.error.main}30`,
                              color: theme.palette.error.main,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        mt: 1.75,
                        pt: 1.75,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap",
                        gap: { xs: 1, sm: 2.5 },
                        alignItems: { sm: "center" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <PersonOutlineIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            opacity: 0.85,
                            flexShrink: 0,
                          }}
                        />
                        <CustomTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Created by
                          </Box>{" "}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {vendorPaymentActorLabel(
                              payment.created_by_name,
                              payment.created_by,
                            )}
                          </Box>
                          {payment.created_at ? (
                            <>
                              {" · "}
                              <Box component="span" sx={{ fontWeight: 500 }}>
                                {formatDate(payment.created_at)}
                              </Box>
                            </>
                          ) : null}
                        </CustomTypography>
                      </Box>
                      {vendorPaymentWasEdited(payment) ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                          }}
                        >
                          <UpdateIcon
                            sx={{
                              fontSize: 18,
                              color: "text.secondary",
                              opacity: 0.85,
                              flexShrink: 0,
                            }}
                          />
                          <CustomTypography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              Last updated by
                            </Box>{" "}
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {vendorPaymentActorLabel(
                                payment.updated_by_name,
                                payment.updated_by,
                              )}
                            </Box>
                            {payment.updated_at ? (
                              <>
                                {" · "}
                                <Box component="span" sx={{ fontWeight: 500 }}>
                                  {formatDate(payment.updated_at)}
                                </Box>
                              </>
                            ) : null}
                          </CustomTypography>
                        </Box>
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </Box>
      </>

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.info.main} 50%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      <>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.purple.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.purple.main}20`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.info.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading5"
                  fontWeight={700}
                  color="info.main"
                >
                  Invoices
                </CustomTypography>
                <CustomTypography variant="body2" color="text.secondary">
                  {
                    purchaseRequestStore.purchaseRequestDetail
                      .purchase_order_invoices?.length
                  }{" "}
                  invoice
                  {purchaseRequestStore.purchaseRequestDetail
                    .purchase_order_invoices?.length !== 1
                    ? "s"
                    : ""}{" "}
                  available
                </CustomTypography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label="All"
                onClick={() => purchaseRequestStore.setPrFilter("ALL")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "ALL"
                    ? "primary"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "ALL"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Pending"
                onClick={() => purchaseRequestStore.setPrFilter("PENDING")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "PENDING"
                    ? "warning"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "PENDING"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Approved"
                onClick={() => purchaseRequestStore.setPrFilter("APPROVED")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "APPROVED"
                    ? "success"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "APPROVED"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Rejected"
                onClick={() => purchaseRequestStore.setPrFilter("REJECTED")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "REJECTED"
                    ? "error"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "REJECTED"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
            </Box>
          </Box>

          {purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices &&
            purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
              .length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 3,
                  mb: 3,
                }}
              >
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Total Uploaded Invoices
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {formatCurrency(invoiceStats.totalUploaded)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.success.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.success.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Approved
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="success.main"
                      >
                        {formatCurrency(invoiceStats.approved)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.warning.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Pending
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="warning.main"
                      >
                        {formatCurrency(invoiceStats.pending)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.error.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Rejected
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="error.main"
                      >
                        {formatCurrency(invoiceStats.rejected)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                </Grid2>
              </Box>
            )}

          {purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices &&
            purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
              .length > 0 && (
              <>
                {filteredInvoices.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {filteredInvoices.map((invoice: PurchaseOrderInvoice) => {
                      return (
                        <Card
                          key={invoice.id}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                                pb: 2,
                                borderBottom: `2px solid ${theme.palette.divider}`,
                                flexWrap: "wrap",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <CustomTypography
                                  variant="heading6"
                                  fontWeight={700}
                                  color="primary.main"
                                >
                                  Invoice #{invoice.invoice_number}
                                </CustomTypography>
                                <Chip
                                  label={`PO #${invoice.po_id}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                                {invoice.invoice_status && (
                                  <Chip
                                    label={invoice.invoice_status}
                                    size="small"
                                    color={
                                      invoice.invoice_status === "APPROVED"
                                        ? "success"
                                        : invoice.invoice_status === "REJECTED"
                                          ? "error"
                                          : "warning"
                                    }
                                    variant="filled"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <IconButton
                                  onClick={() =>
                                    setInvoiceDetailsPopup({
                                      open: true,
                                      invoice,
                                    })
                                  }
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor:
                                      theme.palette.info.main + "15",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.info.main + "25",
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                  size="small"
                                >
                                  <InfoIcon
                                    sx={{
                                      color: "info.main",
                                      fontSize: 18,
                                    }}
                                  />
                                </IconButton>
                                {invoice.invoice_url && (
                                  <AppButton
                                    label="Download"
                                    variant="transparentPrimary"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => {
                                      if (invoice.invoice_url) {
                                        window.open(
                                          invoice.invoice_url,
                                          "_blank",
                                        );
                                      }
                                    }}
                                    customStyles={{ minWidth: 130 }}
                                  />
                                )}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                backgroundColor: theme.palette.info.main + "10",
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.info.main}30`,
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="info.main"
                                fontWeight={500}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {invoice.upload_invoice_admin_name
                                  ? `Invoice uploaded by admin: ${invoice.upload_invoice_admin_name}`
                                  : "Invoice uploaded by vendor Portal"}
                              </CustomTypography>
                            </Box>

                            <Grid2 container spacing={3} sx={{ mb: 3 }}>
                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 86,
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.primary.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Invoice Amount
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    color="primary.main"
                                  >
                                    ₹{invoice.invoice_amount || ""}
                                  </CustomTypography>
                                </Box>
                              </Grid2>

                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 86,
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.info.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Created Date
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    {invoice.created_date
                                      ? formatDate(invoice.created_date)
                                      : "-"}
                                  </CustomTypography>
                                </Box>
                              </Grid2>

                              {invoice.approve_invoice &&
                                invoice.approved_invoice_date && (
                                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box
                                      sx={{
                                        p: 2.5,
                                        border: `1px solid ${theme.palette.success.main}40`,
                                        borderRadius: 2,
                                        backgroundColor:
                                          theme.palette.success.main + "08",
                                        transition: "all 0.2s ease-in-out",
                                        minHeight: 86,
                                        "&:hover": {
                                          boxShadow:
                                            "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                          borderColor:
                                            theme.palette.success.main + "60",
                                        },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        fontWeight={500}
                                        sx={{ mb: 1 }}
                                      >
                                        Approved Date
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                        color="success.main"
                                      >
                                        {formatDate(
                                          invoice.approved_invoice_date,
                                        )}
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )}

                              {invoice.reject_invoice &&
                                invoice.rejected_invoice_date && (
                                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box
                                      sx={{
                                        p: 2.5,
                                        border: `1px solid ${theme.palette.error.main}40`,
                                        borderRadius: 2,
                                        backgroundColor:
                                          theme.palette.error.main + "08",
                                        transition: "all 0.2s ease-in-out",
                                        minHeight: 86,
                                        "&:hover": {
                                          boxShadow:
                                            "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                          borderColor:
                                            theme.palette.error.main + "60",
                                        },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        fontWeight={500}
                                        sx={{ mb: 1 }}
                                      >
                                        Rejected Date
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                        color="error.main"
                                      >
                                        {formatDate(
                                          invoice.rejected_invoice_date,
                                        )}
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )}

                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 80,
                                    display: "flex",
                                    flexDirection: "column",
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.info.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Payment Status
                                  </CustomTypography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body1"
                                      fontWeight={600}
                                      color="error.main"
                                    >
                                      {invoice.invoice_payment_status
                                        ? InvoicePaymentStatusFilters.find(
                                            (filter) =>
                                              filter.id ===
                                              invoice.invoice_payment_status,
                                          )?.value || "Not Set"
                                        : "Not Set"}
                                    </CustomTypography>

                                    <IconButton
                                      sx={{
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                        },
                                        "&:active": {
                                          backgroundColor: "transparent",
                                        },
                                      }}
                                      onClick={(e) =>
                                        invoice.id &&
                                        handlePaymentStatusClick(e, invoice.id)
                                      }
                                    >
                                      <Icon sx={{ fontSize: "12px" }}>
                                        <img
                                          src={pencil_uderline}
                                          alt="edit-icon"
                                        />
                                      </Icon>
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Grid2>
                            </Grid2>
                            {userStore.hasPermission(
                              EmployeeRoles.APPROVE_REJECT_PURCHASE_ORDER_INVOICES,
                            ) && (
                              <>
                                {invoice.invoice_status !== "APPROVED" &&
                                  invoice.invoice_status !== "REJECTED" && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 2,
                                        pt: 2,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                      }}
                                    >
                                      <AppButton
                                        label="Approve"
                                        variant="primary"
                                        onClick={() =>
                                          invoice.id &&
                                          handleApproveInvoice(invoice.id)
                                        }
                                        customStyles={{ minWidth: 120 }}
                                      />
                                      <AppButton
                                        label="Reject"
                                        variant="secondary"
                                        onClick={() =>
                                          invoice.id &&
                                          handleRejectInvoice(invoice.id)
                                        }
                                        customStyles={{ minWidth: 120 }}
                                      />
                                    </Box>
                                  )}

                                {invoice.reject_invoice &&
                                  invoice.rejection_reason && (
                                    <Box
                                      sx={{
                                        mt: 3,
                                        p: 2.5,
                                        backgroundColor:
                                          theme.palette.error.main + "10",
                                        borderRadius: 2,
                                        border: `1px solid ${
                                          theme.palette.error.main + "30"
                                        }`,
                                        borderLeft: `4px solid ${theme.palette.error.main}`,
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        color="error.main"
                                        fontWeight={600}
                                        sx={{ mb: 1 }}
                                      >
                                        Rejection Reason
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        color="error.main"
                                      >
                                        {invoice.rejection_reason}
                                      </CustomTypography>
                                    </Box>
                                  )}
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <CustomTypography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      No{" "}
                      {purchaseRequestStore.prFilter === "ALL"
                        ? ""
                        : purchaseRequestStore.prFilter.toLowerCase()}{" "}
                      invoices found
                    </CustomTypography>
                  </Box>
                )}
              </>
            )}
        </Box>

        <Box
          sx={{
            height: 2,
            background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.success.main} 100%)`,
            borderRadius: 1,
            mb: 4,
          }}
        />
      </>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.info.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.info.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.info.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalculateIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="info.main"
            >
              Summary
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              Financial breakdown and calculations
            </CustomTypography>
          </Box>
        </Box>

        <Card
          sx={{
            border: `2px solid ${theme.palette.primary.main}20`,
            borderRadius: 3,
            boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Calculation Details */}
            <Box sx={{ p: 4 }}>
              <Grid2 container spacing={3}>
                {/* Subtotal */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.info.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Subtotal
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.subtotal || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Before GST calculation
                    </CustomTypography>
                  </Box>
                </Grid2>

                {/* GST */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.warning.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Total GST
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.totalGst || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Tax amount calculated
                    </CustomTypography>
                  </Box>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.secondary.main,
                          mr: 1.5,
                          marginBottom: "0.5rem",
                          marginTop: "0.5rem",
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        style={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Other Charges
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.otherCharges || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Additional fees & charges
                    </CustomTypography>
                  </Box>
                </Grid2>

                {/* Grand Total */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}15 100%)`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="primary.main"
                        fontWeight={600}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Grand Total
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading4"
                      fontWeight={800}
                      color="primary.main"
                    >
                      ₹{totals?.grandTotal || 0}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color="primary.main"
                      fontWeight={500}
                    >
                      Final amount to be paid
                    </CustomTypography>
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.success.main}`,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}08 0%, ${theme.palette.success.main}15 100%)`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.success.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="success.main"
                        fontWeight={600}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Remaining Amount
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading4"
                      fontWeight={800}
                      color="success.main"
                    >
                      ₹{remainingAmountToBePaid.toLocaleString("en-IN")}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color="success.main"
                      fontWeight={500}
                    >
                      Unpaid Amount After Payments
                    </CustomTypography>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {!hasSkuVariants && (
        <SingleInput
          open={purchaseRequestStore.editDialog.open}
          onClose={() => purchaseRequestStore.closeEditDialog()}
          heading={`Edit ${formatFieldName(
            purchaseRequestStore.editDialog.field || "",
          )}`}
          content={`Enter new ${formatFieldName(
            purchaseRequestStore.editDialog.field || "",
          )}`}
          type={
            purchaseRequestStore.editDialog.field === "gst_percentage"
              ? "select"
              : purchaseRequestStore.editDialog.field === "hsn_code"
                ? "text"
                : "number"
          }
          selectOptions={
            purchaseRequestStore.editDialog.field === "gst_percentage"
              ? GstPercentage
              : undefined
          }
          initialValue={
            purchaseRequestStore.editDialog.productId
              ? purchaseRequestStore.purchaseRequestDetail.products
                  ?.find(
                    (product) =>
                      product.product_id ===
                      purchaseRequestStore.editDialog.productId,
                  )
                  ?.[
                    purchaseRequestStore.editDialog
                      .field as keyof PurchaseRequestProduct
                  ]?.toString() || ""
              : ""
          }
          onSubmit={(value) =>
            handleModalSubmit(
              purchaseRequestStore.editDialog.productId || 0,
              value,
              purchaseRequestStore.editDialog.field || "",
            )
          }
          required={purchaseRequestStore.editDialog.field ? true : false}
        />
      )}

      {/* Cancellation Reason Modal */}
      <ConfirmationPopup
        id={purchaseRequestStore.cancellationModal.poId || 0}
        open={purchaseRequestStore.cancellationModal.open}
        title="Cancel Purchase Order"
        content="Please provide a reason for cancelling this purchase order:"
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={handleCancellationSubmit}
        onCancel={handleCancellationClose}
      />

      {/* Short close lines (per line / whole PO) */}
      <ShortClosePoDialog
        open={Boolean(shortCloseTarget)}
        prId={prDetail?.id ?? null}
        poId={shortCloseTarget?.poId ?? null}
        lines={purchaseRequestStore.shortClosePoLines}
        loading={purchaseRequestStore.shortClosePoLinesLoading}
        preselectIds={shortCloseTarget?.preselectIds ?? EMPTY_IDS}
        onClose={() => setShortCloseTarget(null)}
        onSubmit={handleShortCloseSubmit}
      />

      {/* Inwarded qty warning before PO cancel */}
      <ConfirmationPopup
        id={inwardCancelConfirm.poId || 0}
        open={inwardCancelConfirm.open}
        title="PO Already Inwarded"
        content={
          <>
            This purchase order has already been{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                color: "error.main",
              }}
            >
              inwarded
            </Box>
            . Are you sure you still want to cancel it?
          </>
        }
        type="standard"
        onConfirm={handleInwardCancelConfirm}
        onCancel={handleInwardCancelClose}
      />

      {/* Status Change Confirmation Modal */}
      <ConfirmationPopup
        id={purchaseRequestStore.statusChangeModal.poId || 0}
        open={purchaseRequestStore.statusChangeModal.open}
        title="Update Purchase Order Status"
        content={`Are you sure you want to update the status to ${purchaseRequestStore.statusChangeModal.status}?`}
        type="standard"
        onConfirm={handleStatusChangeConfirm}
        onCancel={handleStatusChangeClose}
      />

      {/* Sourcing approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={sourcingApprovePopupOpen}
        title="Approve Sourcing"
        content="Are you sure you want to approve the sourcing stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveSourcingConfirm(id)}
        onCancel={handleApproveSourcingClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={sourcingRejectPopupOpen}
        title="Reject Sourcing"
        content="Please provide a reason for rejecting this purchase request sourcing:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) =>
          handleRejectSourcingConfirm(id, reason || "")
        }
        onCancel={handleRejectSourcingClose}
      />

      {/* Finance approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={financeApprovePopupOpen}
        title="Approve Finance"
        content="Are you sure you want to approve the finance stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveFinanceConfirm(id)}
        onCancel={handleApproveFinanceClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={financeRejectPopupOpen}
        title="Reject Finance"
        content="Please provide a reason for rejecting this purchase request finance:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) => handleRejectFinanceConfirm(id, reason || "")}
        onCancel={handleRejectFinanceClose}
      />

      {/* Swageazy approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={swageazyApprovePopupOpen}
        title="Approve Swageazy"
        content="Are you sure you want to approve the swageazy stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveSwageazyConfirm(id)}
        onCancel={handleApproveSwageazyClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={swageazyRejectPopupOpen}
        title="Reject Swageazy"
        content="Please provide a reason for rejecting this purchase request swageazy:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) =>
          handleRejectSwageazyConfirm(id, reason || "")
        }
        onCancel={handleRejectSwageazyClose}
      />

      {/* Reset approval/rejection confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={sourcingResetPopupOpen}
        title="Reset Sourcing Approval"
        content="Are you sure you want to reset the sourcing approval/rejection for this purchase request?"
        type="standard"
        onConfirm={(id) => handleResetSourcingConfirm(id)}
        onCancel={() => setSourcingResetPopupOpen(false)}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={financeResetPopupOpen}
        title="Reset Finance Approval"
        content="Are you sure you want to reset the finance approval/rejection for this purchase request?"
        type="standard"
        onConfirm={(id) => handleResetFinanceConfirm(id)}
        onCancel={() => setFinanceResetPopupOpen(false)}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={swageazyResetPopupOpen}
        title="Reset Swageazy Approval"
        content="Are you sure you want to reset the Swageazy approval/rejection for this purchase request?"
        type="standard"
        onConfirm={(id) => handleResetSwageazyConfirm(id)}
        onCancel={() => setSwageazyResetPopupOpen(false)}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={resetAllApprovalsPopupOpen}
        title="Reset All Approvals"
        content="Are you sure you want to reset all sourcing, finance, and Swageazy approvals/rejections for this purchase request?"
        type="standard"
        onConfirm={(id) => handleResetAllApprovalsConfirm(id)}
        onCancel={() => setResetAllApprovalsPopupOpen(false)}
      />

      {/* Cancel PR confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={cancelPRPopupOpen}
        title="Cancel Purchase Request"
        content="Please provide a reason for cancelling this purchase request:"
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={(id, reason) => handleCancelPRConfirm(id, reason || "")}
        onCancel={handleCancelPRClose}
      />

      <ConfirmationPopup
        id={reactivatePoId || 0}
        open={reactivatePoPopupOpen}
        title="Reactivate Purchase Order"
        content="Reactivate this purchase order with the updated purchase request details?"
        type="standard"
        onConfirm={(poId) => handleReactivatePOConfirm(poId)}
        onCancel={handleReactivatePoClose}
      />

      {/* Purchase Vouchers Popup */}
      {selectedProductForVouchers && (
        <PurchaseVouchersPopup
          open={purchaseVouchersPopupOpen}
          onClose={() => {
            setPurchaseVouchersPopupOpen(false);
            setSelectedProductForVouchers(null);
          }}
          product={selectedProductForVouchers}
          pr_id={purchaseRequestStore.purchaseRequestDetail.id}
        />
      )}

      <ConfirmationPopup
        id={invoiceStore.approvePurchaseOrderInvoicePopup.invoiceId || 0}
        open={invoiceStore.approvePurchaseOrderInvoicePopup.open}
        title="Approve Invoice"
        content="Are you sure you want to approve this invoice?"
        type="standard"
        onConfirm={(id) => handleApproveInvoiceConfirm(id)}
        onCancel={handleApproveInvoiceClose}
      />

      <ConfirmationPopup
        id={invoiceStore.rejectPurchaseOrderInvoicePopup.invoiceId || 0}
        open={invoiceStore.rejectPurchaseOrderInvoicePopup.open}
        title="Reject Invoice"
        content="Please provide a reason for rejecting this invoice:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) => handleRejectInvoiceConfirm(id, reason || "")}
        onCancel={handleRejectInvoiceClose}
      />

      <ConfirmationPopup
        id={vendorPaymentsStore.deleteState.paymentId || 0}
        open={vendorPaymentsStore.deleteState.open}
        title="Delete Vendor Payment"
        content="Are you sure you want to delete this payment entry?"
        type="standard"
        onConfirm={(id) => handleConfirmDeleteVendorPayment(id)}
        onCancel={() => vendorPaymentsStore.closeDeletePopup()}
      />

      <ConfirmationPopup
        id={removeProductConfirm?.productId || 0}
        open={Boolean(removeProductConfirm)}
        title="Remove Product"
        content={`Remove "${removeProductConfirm?.productName || "this product"}" and all of its SKUs from this purchase request?`}
        type="standard"
        onConfirm={() => void handleConfirmRemoveProduct()}
        onCancel={() => setRemoveProductConfirm(null)}
      />

      <ConfirmationPopup
        id={0}
        open={Boolean(removeSkuConfirm)}
        title="Remove SKU"
        content={`Remove SKU "${removeSkuConfirm?.title || removeSkuConfirm?.sku || ""}" from this purchase request? If it is the last SKU for the product, the product will also be removed.`}
        type="standard"
        onConfirm={() => void handleConfirmRemoveSku()}
        onCancel={() => setRemoveSkuConfirm(null)}
      />

      <VendorPaymentPopup
        open={vendorPaymentsStore.popupState.open}
        onClose={() => vendorPaymentsStore.closePopup()}
        payment={vendorPaymentsStore.popupState.payment}
        onSave={handleSaveVendorPayment}
        loading={vendorPaymentsStore.saving}
        maxAllowedAmount={maxAllowedPaymentAmount}
      />

      <Popover
        open={Boolean(paymentStatusAnchor)}
        anchorEl={paymentStatusAnchor?.element}
        onClose={handlePaymentStatusClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
            mt: 0.5,
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {InvoicePaymentStatusFilters.map((status) => {
            const invoice =
              paymentStatusAnchor &&
              purchaseRequestStore.purchaseRequestDetail &&
              purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices?.find(
                (inv) => inv.id === paymentStatusAnchor.invoiceId,
              );

            const isSelected =
              invoice?.invoice_payment_status !== undefined &&
              invoice.invoice_payment_status !== null &&
              invoice.invoice_payment_status === status.id;

            return (
              <MenuItem
                key={status.id}
                onClick={() => handlePaymentStatusSelect(status.id)}
                selected={isSelected || false}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main + "15",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "25",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={status.value}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Popover>

      {invoiceStore.invoiceUploadState.selectedPoId && (
        <InvoiceUploadDialog
          open={invoiceStore.invoiceUploadState.dialogOpen}
          onClose={handleCloseInvoiceUpload}
          onUpload={handleInvoiceUpload}
          products={prDetail.products || []}
          skuVariants={prDetail.sku_variants || []}
          invoices={
            purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices?.filter(
              (inv) =>
                inv.po_id === invoiceStore.invoiceUploadState.selectedPoId,
            ) || []
          }
          poId={invoiceStore.invoiceUploadState.selectedPoId}
          vendorId={invoiceStore.invoiceUploadState.vendorId || 0}
          isUploading={invoiceStore.invoiceUploadState.isUploading}
        />
      )}

      <PurchaseRequestActivityLogsDrawer
        open={activityLogsDrawerOpen}
        onClose={() => setActivityLogsDrawerOpen(false)}
        prId={prId ? Number(prId) : null}
      />

      <Dialog
        open={invoiceDetailsPopup.open}
        onClose={() => setInvoiceDetailsPopup({ open: false, invoice: null })}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2,
          }}
        >
          <CustomTypography variant="heading5" fontWeight={600}>
            Invoice #
            {invoiceDetailsPopup.invoice?.invoice_number ||
              invoiceDetailsPopup.invoice?.id}{" "}
            - Details
          </CustomTypography>
          <IconButton
            onClick={() =>
              setInvoiceDetailsPopup({ open: false, invoice: null })
            }
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {invoiceDetailsPopup.invoice && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box
                sx={{
                  p: 2,
                  mt: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <CustomTypography
                  variant="body1"
                  fontWeight={600}
                  style={{ marginBottom: "0.5rem" }}
                >
                  Other Charges
                </CustomTypography>
                <CustomTypography
                  variant="heading6"
                  color="primary.main"
                  fontWeight={700}
                >
                  ₹
                  {invoiceDetailsPopup.invoice.other_charges !== undefined &&
                  invoiceDetailsPopup.invoice.other_charges !== null
                    ? Number(
                        invoiceDetailsPopup.invoice.other_charges,
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : "0.00"}
                </CustomTypography>
              </Box>

              <InvoiceItemsDetailsTable
                products={invoiceDetailsPopup.invoice.invoice_products || []}
                skuVariants={prDetail.sku_variants}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button
            onClick={() =>
              setInvoiceDetailsPopup({ open: false, invoice: null })
            }
            variant="contained"
            sx={{
              minWidth: 100,
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid2>
  );
};

export default observer(PurchaseRequestDetail);
